import Phaser from 'phaser';
import { GAME_CONFIG, PLAYER_CONFIG, PHYSICS_CONFIG, PROJECTILE_CONFIG, LIVES_CONFIG, ENERGY_CONFIG, SCENE_KEYS, ASSET_KEYS, screenShakeState, designModeState } from '@/config/constants';
import { AudioManager } from '@/utils/AudioManager';
import { ImpactSystem } from '@/utils/ImpactSystem';
import { AmbientWorldSetup } from '@/utils/AmbientWorldSetup';
import { AIPlayer } from '@/utils/AIPlayer';
import { ANIMATION_KEYS, FRAME_KEYS } from '@/utils/AnimationManager';
import { BackgroundManager } from '@/utils/BackgroundManager';
import { submitScore, submitStageScore } from '@/utils/SupabaseClient';
import { AchievementManager } from '@/utils/AchievementManager';
import { PostFXManager } from '@/utils/PostFXManager';
import { KioskWatchdog } from '@/utils/KioskWatchdog';
import { transitionTo } from '@/utils/UIAnimations';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { createSubtleLink } from '@/ui/SubtleLink';
import { GamepadNav } from '@/utils/GamepadNav';
import { NarrativeCardModal } from '@/ui/NarrativeCardModal';
import { BOSS_INTRO } from '@/utils/NarrativeCards';
import { COLORS, FONTS } from '@/ui/theme';

// HUD palette — mirrors UIScene's energy bar so the boss player HUD matches the
// normal stages exactly (same trough, fill colors, sheen, life stars).
const HUD_NAVY = 0x0C1A2E;
const HUD_NAVY_DEEP = 0x05101D;
const HUD_GREEN = 0x3FD24A;
const HUD_GREEN_LIGHT = 0x7CE89A;

/**
 * Boss Scene - Pollution Hydra Final Boss (AAA Quality)
 * 3 attack types, phase transitions, stance sprites, epic death sequence
 */
export class BossScene extends Phaser.Scene {
  // Player
  private player?: Phaser.GameObjects.Sprite;
  private playerShadow?: Phaser.GameObjects.Ellipse;
  private victoryNav?: GamepadNav;
  // CREDITS link on the victory card. Created in showVictory(); referenced by
  // showVictoryMenuButtons() so the joystick / keyboard can focus it too.
  private victoryCreditsLink?: Phaser.GameObjects.Container;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private spaceKey?: Phaser.Input.Keyboard.Key;
  private gamepad?: Phaser.Input.Gamepad.Gamepad;
  private prevGamepadShoot = false;
  private playerState: 'idle' | 'walking' | 'shooting' = 'idle';

  // Boss
  private boss?: Phaser.GameObjects.Sprite;
  // The boss's body glow filter — kept as a ref so phase transitions can
  // tweak its outerStrength/color (v4 doesn't expose `.preFX.list[0]`).
  private bossGlow?: Phaser.Filters.Glow;
  private bossHP = 18;
  private maxBossHP = 18;
  private bossPhase = 1;
  private bossAttackTimer?: Phaser.Time.TimerEvent;
  private hpBarBg?: Phaser.GameObjects.Rectangle;
  private bossNameText?: Phaser.GameObjects.Text;
  private bossBaseX = GAME_CONFIG.WIDTH / 2;
  private bossBaseY = 280;
  private bossTargetX = GAME_CONFIG.WIDTH / 2;
  private bossInvincible = false; // Invincible during attacks & transitions
  private bossHitCount = 0; // Track hits for shield mechanic
  private shieldActive = false;
  private shieldGraphic?: Phaser.GameObjects.Arc;
  private weakWindowActive = false;
  private weakWindowHitsRemaining = 0;
  private weakWindowTimer?: Phaser.Time.TimerEvent;
  private weakSpotGlow?: Phaser.GameObjects.Arc;
  private weakSpotSpark?: Phaser.GameObjects.Arc;
  // ── TRIDENT LOCK (Oceanic super-target for weak windows) ──
  private tridentLockGroup?: Phaser.GameObjects.Container;
  private tridentLockBeacon?: Phaser.GameObjects.Container;
  private tridentLockUpdateHandler?: () => void;

  // Projectiles (player)
  private projectileSprites: Phaser.GameObjects.Image[] = [];
  private isProjectileActive = false;
  private projectileStartX = 0;
  private lastShotTime = 0; // Shooting cooldown
  private readonly SHOT_COOLDOWN = 150; // ms between shots (matches GameScene feel)

  // Boss balls (using any for flexible access to x/y/data)
  private balls: any[] = [];

  // Particles
  private toxicEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private popEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  // Arena hazards
  private toxicPools: Phaser.GameObjects.Ellipse[] = [];
  private fogGraphics?: Phaser.GameObjects.Graphics;
  private fogHeight = 100;

  // Game state
  private lives = LIVES_CONFIG.MAX_LIVES;
  private isInvincible = false;
  private isGameOver = false;

  // Player energy HUD — drawn in this scene so it can't be hidden behind the
  // arena like the shared UIScene HUD. Mirrors the normal-stage HUD: a LIVES
  // row (label + green stars) above an ENERGY row (label + per-life bar).
  private playerHpBg?: Phaser.GameObjects.Graphics;
  private playerHpFill?: Phaser.GameObjects.Graphics;
  private playerHpGlow?: Phaser.GameObjects.Graphics;
  private playerHpLabel?: Phaser.GameObjects.Text;
  private livesLabel?: Phaser.GameObjects.Text;
  private lifeStars: Phaser.GameObjects.Graphics[] = [];
  private readonly PLAYER_HP_X = 44;
  private readonly PLAYER_HP_Y = 40;
  private readonly PLAYER_HP_BAR_W = 240;
  private readonly PLAYER_HP_BAR_H = 18;
  // Energy bar geometry, stashed for the per-frame redraw.
  private energyBarCx = 0;
  private energyBarCy = 0;
  private score = 0;
  private startingScore = 0;
  private isTransitioning = false; // Phase transition lock

  // Background
  private backgroundManager?: BackgroundManager;
  // bgImage stored for potential ocean restoration effect

  // "Summoning the Hydra" veil — only shown when preload() has to re-fetch
  // boss art that BootScene dropped on a poor connection (see preload()).
  private bossLoadVeil?: Phaser.GameObjects.Container;

  // Autoplay / demo mode
  public isAutoPlay: boolean = false;
  private aiPlayer?: AIPlayer;
  private autoShootTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super({ key: SCENE_KEYS.BOSS });
  }

  init(data?: { score?: number; autoPlay?: boolean }): void {
    this.startingScore = data?.score || 0;
    this.score = this.startingScore;
    this.isAutoPlay = data?.autoPlay || false;
    // Exhibition tuning: boss HP set to 18 — a ~30% cut from the old 25 so the
    // Hydra dies faster (18 hits = a clean 6/6/6 split across the three phases).
    // The HP bar renders as bossHP / maxBossHP, so starting both at the same
    // value paints the bar end to end at game start. Phase transitions and
    // visual thresholds below are scaled to this cap (proportions preserved, so
    // the phase banners, enrage, and damaged sprite all fire at the same points).
    this.bossHP = 18;
    this.maxBossHP = 18;
    this.bossPhase = 1;
    this.lives = LIVES_CONFIG.MAX_LIVES;
    this.isInvincible = false;
    this.isGameOver = false;
    this.isTransitioning = false;
    this.balls = [];
    this.toxicPools = [];
    this.projectileSprites = [];
    this.isProjectileActive = false;
    this.fogHeight = 100;
    this.bossTargetX = GAME_CONFIG.WIDTH / 2;
    this._victoryShown = false;
    this.bossInvincible = false;
    this.bossHitCount = 0;
    this.shieldActive = false;
    this.weakWindowActive = false;
    this.weakWindowHitsRemaining = 0;
    this.lastShotTime = 0;
    this.stanceTimer = 0;
    this.currentStance = 'idle';
    // Cleared each run so the victory-menu nav never references a destroyed
    // link from a previous boss fight.
    this.victoryCreditsLink = undefined;
  }

  /**
   * Guarantee the boss arena's critical art is in the texture cache before
   * create() runs.
   *
   * Every asset is normally loaded once in BootScene, but on a poor connection
   * the boss art can be missing by the time the player reaches stage 7:
   *   1. BootScene's 60s stall watchdog force-completes the loader (load.reset)
   *      while a cold download is still going, dropping whatever is still
   *      queued. The boss frames are queued late, so they go first.
   *   2. A flaky request can drop a single file and BootScene has no per-asset
   *      retry.
   * When `boss_frame_1` is missing, createBoss() used to no-op, the boss was
   * null, and create() took a fallback branch that started NO attacks: a dead
   * arena (hydra backdrop, no boss, no fight) that reads to the player as a
   * broken / "game over" screen a moment after the hydra appears.
   *
   * Re-queue anything missing here. Phaser blocks create() until preload's
   * queue drains, so the scene can no longer start without its boss. A short
   * veil covers the (usually sub-second) top-up; a couple of retries cover a
   * dropped request.
   */
  preload(): void {
    const missing: Array<{ key: string; path: string }> = [];
    const want = (key: string, path: string): void => {
      if (!this.textures.exists(key)) missing.push({ key, path });
    };

    for (let i = 1; i <= 41; i++) {
      want(`boss_frame_${i}`, `assets/sprites/boss/boss_${i.toString().padStart(3, '0')}.png`);
    }
    want('boss_attack', 'assets/sprites/boss/boss_attack.png');
    want('boss_lunge', 'assets/sprites/boss/boss_lunge.png');
    want('boss_damaged', 'assets/sprites/boss/boss_damaged.png');
    want('boss_death', 'assets/sprites/boss/boss_death.png');
    for (let i = 1; i <= 4; i++) want(`boss_pain_${i}`, `assets/sprites/boss/boss_pain_${i}.png`);
    for (let i = 1; i <= 4; i++) want(`boss_death_anim_${i}`, `assets/sprites/boss/boss_death_anim_${i}.png`);
    for (let i = 1; i <= 4; i++) want(`boss_sludge_${i}`, `assets/sprites/boss/boss_sludge_${i}.png`);
    want('boss_bg.jpg', 'assets/backgrounds/images/boss_bg.jpg');
    want('boss_intro', 'assets/ui/education/boss_intro.png');

    if (missing.length === 0) return; // Happy path: boot loaded everything.

    console.warn(`⚠️ BossScene.preload: ${missing.length} boss assets missing after boot — re-loading before the fight starts`);
    this.showBossLoadVeil();

    // Per-asset retry: a dropped request on a flaky link re-queues up to twice
    // rather than leaving the texture missing for the whole fight. Removed on
    // 'complete' so the handler can't accumulate across repeated boss entries.
    const retries: Record<string, number> = {};
    const onLoadError = (file: { key?: string }): void => {
      const key = file?.key || '';
      const found = missing.find((m) => m.key === key);
      if (!found) return;
      retries[key] = (retries[key] || 0) + 1;
      if (retries[key] <= 2) {
        console.warn(`⚠️ BossScene.preload: retry ${retries[key]} for '${key}'`);
        this.load.image(found.key, found.path);
      }
    };
    this.load.on('loaderror', onLoadError);
    this.load.once('complete', () => this.load.off('loaderror', onLoadError));

    for (const m of missing) this.load.image(m.key, m.path);
  }

  /** Full-screen cover shown while preload() tops up missing boss art. */
  private showBossLoadVeil(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const bg = this.add.rectangle(0, 0, WIDTH, HEIGHT, HUD_NAVY_DEEP, 1).setOrigin(0, 0);
    const label = this.add.text(WIDTH / 2, HEIGHT / 2, 'SUMMONING THE HYDRA', {
      fontFamily: FONTS.display, fontSize: '44px', color: '#9EE8F4',
      stroke: '#05101D', strokeThickness: 6, letterSpacing: 2,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5);
    this.bossLoadVeil = this.add.container(0, 0, [bg, label]).setDepth(100000);
  }

  /** Drop the summoning veil once the arena (and re-loaded art) is built. */
  private hideBossLoadVeil(): void {
    if (!this.bossLoadVeil) return;
    const veil = this.bossLoadVeil;
    this.bossLoadVeil = undefined;
    this.tweens.add({ targets: veil, alpha: 0, duration: 250, onComplete: () => veil.destroy() });
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // ── DEFENSIVE FILTER RESET (autoplay restart-safe) ──────────────────
    // v4 cameras retain their FilterList across scene restarts; without
    // an explicit clear, PostFXManager.attach below would stack a fresh
    // CinematicFilter on top of leftovers from the previous run.
    const cam0 = this.cameras.main;
    cam0.filters?.internal.clear();
    cam0.filters?.external.clear();

    // ── TRIPLE-AAA CINEMATIC POST-FX STACK (boss preset - heavier bloom + vignette) ──
    PostFXManager.attach(this, 'boss');

    // Background — animated Hydra arena video (crossfade-looped by BackgroundManager).
    // `image: 'boss_bg.jpg'` is the still-image safety net used by
    // BackgroundManager.fallbackToImage() if video playback fails to start
    // (decoder hiccup, autoplay-policy denial, etc.). Without this the
    // fallback drops to a sky-blue gradient and the arena visibly
    // "disappears" mid-fight.
    this.backgroundManager = new BackgroundManager(this);
    this.backgroundManager.createBackground({
      type: 'video',
      video: 'hydra-stage.mp4',
      image: 'boss_bg.jpg',
      scaleMode: 'cover',
      opacity: 1.0,
    });

    // Background stored for ocean restoration

    // Explicit shutdown handler — releases native HTMLVideoElement listeners
    // and the crossfade poll timer. Phaser's implicit GameObject teardown
    // does NOT remove these, and over a 4-day exhibition cycle the BossScene
    // is entered hundreds of times.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.backgroundManager?.destroyBackground();
      this.backgroundManager = undefined;
      // Clear the victory-card flag so the idle watchdog doesn't keep watching
      // after we leave the boss scene.
      KioskWatchdog.instance.setBossVictory(false);
    });

    // Vignette
    const vig = this.add.graphics();
    vig.setDepth(400).setAlpha(0.5);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.7, 0.7, 0, 0);
    vig.fillRect(0, 0, WIDTH, 150);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.7, 0.7);
    vig.fillRect(0, HEIGHT - 120, WIDTH, 120);

    // Audio
    const audioManager = AudioManager.getInstance();
    audioManager.initialize(this);
    audioManager.playMusic('hydra');

    // AAA: Boss ambient — dying fish, plastic debris, toxic bubbles, soot
    this.installBossParallax();
    AmbientWorldSetup.installBoss(this);

    // Particle textures
    if (!this.textures.exists('pixel')) {
      const g = this.add.graphics();
      g.fillStyle(0xFFFFFF, 1).fillCircle(8, 8, 8);
      g.generateTexture('pixel', 16, 16);
      g.destroy();
    }

    // Toxic drip emitter
    this.toxicEmitter = this.add.particles(0, 0, 'pixel', {
      speed: { min: 10, max: 40 }, angle: { min: 80, max: 100 },
      scale: { start: 1.0, end: 0 }, alpha: { start: 0.6, end: 0 },
      lifespan: 500, tint: [0x44FF44, 0x22CC22, 0x88FF44],
      gravityY: 200, frequency: -1, blendMode: 'ADD',
    }).setDepth(45);

    // Pop emitter
    this.popEmitter = this.add.particles(0, 0, 'pixel', {
      speed: { min: 100, max: 300 }, angle: { min: 0, max: 360 },
      scale: { start: 1.5, end: 0 }, alpha: { start: 0.8, end: 0 },
      lifespan: 400, tint: [0x44FF44, 0x88FF44, 0xAAFF66],
      gravityY: 300, frequency: -1, blendMode: 'SCREEN',
    }).setDepth(999);

    // Arena atmosphere
    this.createArenaAtmosphere();

    // Player
    this.createPlayer();
    this.setupInput();

    // Boss
    this.createBoss();
    this.createHPBar();

    // UI — stop any prior UIScene to prevent double-launch conflicts
    if (this.scene.isActive(SCENE_KEYS.UI)) this.scene.stop(SCENE_KEYS.UI);
    this.scene.launch(SCENE_KEYS.UI, { startingScore: this.score, lives: this.lives, layout: 'boss' });

    // Player health is drawn directly in THIS scene (buildPlayerHealthHud)
    // because the shared UIScene HUD renders behind the full-screen boss arena
    // (scene order), so it can't be hidden — the player always sees their HP.
    this.buildPlayerHealthHud();
    // Delay HUD sync — UIScene needs a frame to run its create() before setText works
    this.time.delayedCall(100, () => {
      this.syncBossHudStatus('PHASE 1', '#9EE8F4');
      this.syncScore();
    });

    // Boss intro — narrative card first (Hydra portrait + lore copy from
    // the client brief), then the existing in-scene warning cinematic.
    // Skip the modal entirely in autoplay so the AI loop isn't gated on
    // a click. Modal hides the boss HUD until dismissed so the card sits
    // cleanly over the arena background.
    if (this.boss) {
      if (this.isAutoPlay) {
        this.showBossIntro();
      } else {
        this.runBossIntroCard(() => this.showBossIntro());
      }
    } else {
      console.warn('⚠️ Boss sprite failed to create — skipping intro, starting attacks with fallback');
      this.bossStartTime = this.time.now;
    }

    // AAA: Autoplay AI setup
    if (this.isAutoPlay) {
      this.setupAutoplayAI();
    }

    // Arena is fully built — drop the "summoning" veil if preload() raised one.
    this.hideBossLoadVeil();
  }

  /**
   * Setup autoplay AI: AI controls player movement (dodge balls) and
   * auto-shoots on a timer. When designMode is on, the player is
   * practically invincible (hits are bypassed), so autoplay can always
   * complete the boss even if the AI is suboptimal.
   */
  private setupAutoplayAI(): void {
    this.aiPlayer = new AIPlayer();

    // Watermark
    const label = this.add.text(GAME_CONFIG.WIDTH - 30, GAME_CONFIG.HEIGHT - 30, 'AUTO-PLAY', {
      fontSize: '48px',
      fontFamily: 'CF Asty Std',
      color: '#00FF88',
      stroke: '#000000',
      strokeThickness: 4,
    });
    label.setOrigin(1, 1).setAlpha(0.6).setDepth(10000);

    // Auto-shoot timer — fires every 600ms while the boss is alive
    this.autoShootTimer = this.time.addEvent({
      delay: 600,
      loop: true,
      callback: () => {
        if (this.isGameOver || this.isTransitioning || !this.boss) return;
        if (!this.isProjectileActive) this.shootProjectile();
      },
    });
  }

  /**
   * Run autoplay AI each frame — moves player based on AI decisions.
   * Targets the boss position (so the shoots hit it) and dodges balls.
   */
  private runAutoplayAI(delta: number): void {
    if (!this.aiPlayer || !this.player) return;

    // Build ball list for AI (same shape as GameScene balls)
    const ballList = this.balls.map(b => ({
      x: b.x,
      y: b.y,
      radius: b.getData('radius') || 32,
      vx: b.getData('velocityX') || 0,
      vy: b.getData('velocityY') || 0,
    }));

    // Also treat the boss as a "ball" to shoot at if alive
    if (this.boss) {
      ballList.push({
        x: this.boss.x,
        y: this.boss.y,
        radius: 120,
        vx: 0,
        vy: -1, // Pretend ascending so AI doesn't flee
      });
    }

    const input = this.aiPlayer.getInput(this.player.x, this.player.y, ballList, delta);

    // Apply movement directly (bypass cursor checks)
    const moveDistance = (PLAYER_CONFIG.SPEED * delta) / 1000;
    if (input.left) {
      this.player.x -= moveDistance;
      this.player.setFlipX(true);
    }
    if (input.right) {
      this.player.x += moveDistance;
      this.player.setFlipX(false);
    }
  }

  // Track how long boss has been in a non-idle stance for safety reset
  private stanceTimer = 0;
  private readonly STANCE_SAFETY_MS = 3000; // Force idle after 3s stuck in any stance

  /** Get the correct base scale for the current phase */
  private getPhaseScale(): number {
    return this.bossPhase === 1 ? 0.7 : this.bossPhase === 2 ? 0.8 : 0.9;
  }

  private getWeakWindowHitCap(): number {
    // Exhibition tuning: every weak window allows one extra core hit so each
    // "STRIKE NOW!" opening burns more HP off the boss.
    return this.bossPhase === 1 ? 5 : this.bossPhase === 2 ? 6 : 7;
  }

  private syncBossHudStatus(label: string, color: string = '#9EE8F4'): void {
    const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
    if (uiScene?.updateBossStatus) {
      try { uiScene.updateBossStatus(label, 'HYDRA', color); } catch {}
    }
  }

  /** Push current score to the UIScene HUD */
  private syncScore(): void {
    const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
    if (uiScene?.setScore) {
      try { uiScene.setScore(this.score); } catch {}
    }
  }

  private openWeakWindow(durationMs: number, hitsAllowed: number, banner: string = 'STRIKE THE CORE!'): void {
    if (!this.boss || this.isGameOver) return;

    this.closeWeakWindow();
    this.weakWindowActive = true;
    this.weakWindowHitsRemaining = hitsAllowed;
    this.bossInvincible = false;

    // ── 🔱 TRIDENT LOCK — Oceanic super-target ──
    this.buildTridentLock(this.boss.x, this.boss.y + 54);

    // Keep legacy glow fields as subtle inner core (additive)
    this.weakSpotGlow = this.add.circle(this.boss.x, this.boss.y + 54, 38, 0xFFD700, 0.35);
    this.weakSpotGlow.setDepth(56).setBlendMode(Phaser.BlendModes.ADD);

    this.weakSpotSpark = this.add.circle(this.boss.x, this.boss.y + 54, 14, 0xFFFFFF, 0.85);
    this.weakSpotSpark.setDepth(57).setBlendMode(Phaser.BlendModes.ADD);

    this.tweens.add({
      targets: [this.weakSpotGlow, this.weakSpotSpark],
      scale: { from: 0.8, to: 1.25 },
      alpha: { from: 0.35, to: 0.95 },
      duration: 260,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.showAttackName(banner, '#FFD700', '#3D1E00');
    this.syncBossHudStatus(`STRIKE ${hitsAllowed}`, '#FFD700');

    // ── Audio + PostFX punch to announce the window ──
    try {
      const AudioManagerAny = AudioManager.getInstance();
      AudioManagerAny.playSFX(this, 'sfx_boss_phase', { volume: 0.65 });
      AudioManagerAny.playSFX(this, 'sfx_go', { volume: 0.5, detune: 300 } as any);
    } catch {}
    try {
      // pulse the cinematic pipeline for a brief gold flash
      import('@/utils/PostFXManager').then(({ PostFXManager }) => {
        PostFXManager.get(this)?.ultFlash(500);
      });
    } catch {}

    this.weakWindowTimer = this.time.delayedCall(durationMs, () => {
      this.closeWeakWindow();
    });
  }

  /**
   * Build the Oceanic super-target: golden trident reticle with rotating
   * compass rose, pulsing aura, "STRIKE" label, and a beacon arrow pointing
   * from the top of the screen. Everything tracks the Hydra's core.
   */
  private buildTridentLock(x: number, y: number): void {
    if (!this.boss) return;

    const container = this.add.container(x, y).setDepth(55);

    // ─── 1. Outer rotating compass-rose ring (8 triangular teeth) ───
    const compass = this.add.graphics();
    const drawCompass = (tint: number, thickness: number) => {
      compass.clear();
      compass.lineStyle(thickness, tint, 0.95);
      // 8 cardinal/ordinal tick marks pointing inward (compass rose style)
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const r1 = 94;
        const r2 = i % 2 === 0 ? 74 : 82;
        compass.beginPath();
        compass.moveTo(Math.cos(a) * r1, Math.sin(a) * r1);
        compass.lineTo(Math.cos(a + 0.05) * r2, Math.sin(a + 0.05) * r2);
        compass.lineTo(Math.cos(a - 0.05) * r2, Math.sin(a - 0.05) * r2);
        compass.closePath();
        compass.strokePath();
        compass.fillStyle(tint, 0.28);
        compass.fillPath();
      }
      // Outer ring
      compass.lineStyle(thickness + 1, tint, 0.7);
      compass.strokeCircle(0, 0, 94);
      // Inner ring
      compass.lineStyle(thickness, tint, 0.4);
      compass.strokeCircle(0, 0, 60);
    };
    drawCompass(0xFFD700, 3);
    compass.setBlendMode(Phaser.BlendModes.ADD);
    container.add(compass);

    // ─── 2. Counter-rotating inner ring (cyan — Poseidon's cold fury) ───
    const innerRing = this.add.graphics();
    innerRing.lineStyle(2, 0x9EE8F4, 0.8);
    innerRing.strokeCircle(0, 0, 52);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      innerRing.lineStyle(2, 0x9EE8F4, 0.8);
      innerRing.beginPath();
      innerRing.moveTo(Math.cos(a) * 48, Math.sin(a) * 48);
      innerRing.lineTo(Math.cos(a) * 56, Math.sin(a) * 56);
      innerRing.strokePath();
    }
    innerRing.setBlendMode(Phaser.BlendModes.ADD);
    container.add(innerRing);

    // ─── 3. The TRIDENT glyph centered ───
    // Three vertical prongs with a crossbar — code-drawn so it always hits.
    const trident = this.add.graphics();
    const drawTrident = () => {
      trident.clear();
      // Center prong
      trident.fillStyle(0xFFF4C2, 1);
      trident.lineStyle(3, 0x5B3900, 0.8);
      trident.fillRect(-3, -36, 6, 44);
      trident.strokeRect(-3, -36, 6, 44);
      // Left prong
      trident.fillRect(-22, -30, 6, 24);
      trident.strokeRect(-22, -30, 6, 24);
      // Right prong
      trident.fillRect(16, -30, 6, 24);
      trident.strokeRect(16, -30, 6, 24);
      // Pointed tips (triangle shapes)
      trident.fillStyle(0xFFD700, 1);
      // Center tip
      trident.fillTriangle(-5, -34, 5, -34, 0, -46);
      // Left tip
      trident.fillTriangle(-24, -28, -14, -28, -19, -40);
      // Right tip
      trident.fillTriangle(14, -28, 24, -28, 19, -40);
      // Crossbar
      trident.fillStyle(0xB8892C, 1);
      trident.fillRect(-26, -8, 52, 5);
      trident.strokeLineShape(new Phaser.Geom.Line(-26, -8, 26, -8));
      trident.strokeLineShape(new Phaser.Geom.Line(-26, -3, 26, -3));
      // Handle base
      trident.fillStyle(0x8B5A2B, 1);
      trident.fillRect(-3, 8, 6, 18);
      trident.strokeRect(-3, 8, 6, 18);
    };
    drawTrident();
    trident.setBlendMode(Phaser.BlendModes.NORMAL);
    container.add(trident);

    // ─── 4. "STRIKE" label above the target ───
    const strikeLabel = this.add.text(0, -130, 'STRIKE', {
      fontFamily: 'CF Asty Std',
      fontSize: '36px',
      color: '#FFF4C2',
      stroke: '#3D1E00',
      strokeThickness: 6,
      fontStyle: 'italic',
      letterSpacing: 4,
      shadow: { offsetX: 0, offsetY: 4, color: '#000000', blur: 12, fill: true },
    } as any).setOrigin(0.5);
    container.add(strikeLabel);

    // ─── 5. 4 directional arrows converging on the target ───
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const arrow = this.add.graphics();
      arrow.fillStyle(0xFFD700, 0.9);
      arrow.lineStyle(2, 0x3D1E00, 1);
      const ax = Math.cos(a) * 130;
      const ay = Math.sin(a) * 130;
      // Arrow shape (pointing inward toward center)
      arrow.beginPath();
      arrow.moveTo(ax, ay);
      arrow.lineTo(ax + Math.cos(a + 2.6) * 20, ay + Math.sin(a + 2.6) * 20);
      arrow.lineTo(ax + Math.cos(a - 2.6) * 20, ay + Math.sin(a - 2.6) * 20);
      arrow.closePath();
      arrow.fillPath();
      arrow.strokePath();
      arrow.setBlendMode(Phaser.BlendModes.ADD);
      container.add(arrow);
      // Pulse arrows inward toward the target
      this.tweens.add({
        targets: arrow,
        x: -Math.cos(a) * 30,
        y: -Math.sin(a) * 30,
        alpha: { from: 1, to: 0.35 },
        duration: 520,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: i * 80,
      });
    }

    // ─── 6. Rotate compass + counter-rotate inner ring ───
    this.tweens.add({
      targets: compass,
      angle: 360,
      duration: 4200,
      repeat: -1,
      ease: 'Linear',
    });
    this.tweens.add({
      targets: innerRing,
      angle: -360,
      duration: 2800,
      repeat: -1,
      ease: 'Linear',
    });

    // ─── 7. Trident bob + scale pulse ───
    this.tweens.add({
      targets: trident,
      y: -6,
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: container,
      scale: { from: 0.95, to: 1.1 },
      duration: 380,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // ─── 8. STRIKE label bob + letter-wave ───
    this.tweens.add({
      targets: strikeLabel,
      y: -140,
      alpha: { from: 0.7, to: 1 },
      duration: 340,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.tridentLockGroup = container;

    // ─── 9. BEACON at top of screen pointing down ───
    // So the player sees the cue even if they're looking away from Hydra.
    const beacon = this.add.container(x, 140).setDepth(97);
    const beaconLabel = this.add.text(0, -24, 'WEAK CORE EXPOSED', {
      fontFamily: 'CF Asty Std',
      fontSize: '22px',
      color: '#FFF4C2',
      stroke: '#3D1E00',
      strokeThickness: 4,
      fontStyle: 'italic',
      letterSpacing: 3,
      shadow: { offsetX: 0, offsetY: 2, color: '#000000', blur: 8, fill: true },
    } as any).setOrigin(0.5);
    const beaconArrow = this.add.graphics();
    beaconArrow.fillStyle(0xFFD700, 1);
    beaconArrow.lineStyle(3, 0x3D1E00, 1);
    // Down-pointing arrow
    beaconArrow.beginPath();
    beaconArrow.moveTo(0, 16);
    beaconArrow.lineTo(-14, -4);
    beaconArrow.lineTo(-6, -4);
    beaconArrow.lineTo(-6, -14);
    beaconArrow.lineTo(6, -14);
    beaconArrow.lineTo(6, -4);
    beaconArrow.lineTo(14, -4);
    beaconArrow.closePath();
    beaconArrow.fillPath();
    beaconArrow.strokePath();
    beacon.add([beaconLabel, beaconArrow]);
    this.tweens.add({
      targets: beacon,
      y: 155,
      duration: 340,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tridentLockBeacon = beacon;

    // ─── 10. Continuously track the boss's X position ───
    this.tridentLockUpdateHandler = () => {
      if (!this.boss || !this.tridentLockGroup) return;
      this.tridentLockGroup.x = this.boss.x;
      this.tridentLockGroup.y = this.boss.y + 54;
      if (this.tridentLockBeacon) {
        this.tridentLockBeacon.x = this.boss.x;
      }
    };
    this.events.on(Phaser.Scenes.Events.UPDATE, this.tridentLockUpdateHandler);
  }

  private closeWeakWindow(showStatus: boolean = true): void {
    this.weakWindowActive = false;
    this.weakWindowHitsRemaining = 0;

    if (this.weakWindowTimer) {
      this.weakWindowTimer.remove(false);
      this.weakWindowTimer = undefined;
    }

    // ── Tear down the TRIDENT LOCK ──
    if (this.tridentLockUpdateHandler) {
      this.events.off(Phaser.Scenes.Events.UPDATE, this.tridentLockUpdateHandler);
      this.tridentLockUpdateHandler = undefined;
    }
    if (this.tridentLockGroup) {
      const g = this.tridentLockGroup;
      this.tweens.killTweensOf(g);
      this.tweens.add({
        targets: g,
        alpha: 0,
        scale: 2,
        duration: 220,
        ease: 'Quad.easeOut',
        onComplete: () => g.destroy(),
      });
      this.tridentLockGroup = undefined;
    }
    if (this.tridentLockBeacon) {
      const b = this.tridentLockBeacon;
      this.tweens.killTweensOf(b);
      this.tweens.add({
        targets: b,
        alpha: 0,
        y: b.y - 20,
        duration: 220,
        ease: 'Quad.easeIn',
        onComplete: () => b.destroy(),
      });
      this.tridentLockBeacon = undefined;
    }

    const weakObjects = [this.weakSpotGlow, this.weakSpotSpark].filter(Boolean) as Phaser.GameObjects.Arc[];
    weakObjects.forEach((obj) => {
      this.tweens.killTweensOf(obj);
      this.tweens.add({
        targets: obj,
        alpha: 0,
        scale: 1.5,
        duration: 180,
        onComplete: () => obj.destroy(),
      });
    });
    this.weakSpotGlow = undefined;
    this.weakSpotSpark = undefined;

    if (showStatus) {
      const label = this.bossPhase === 3 && this.bossHP <= 4 ? 'ENRAGED' : `PHASE ${this.bossPhase}`;
      const color = this.bossPhase === 1 ? '#9EE8F4' : this.bossPhase === 2 ? '#F5A623' : '#D94F3A';
      this.syncBossHudStatus(label, color);

      // ── TRIPLE-AAA: PostFX phase theming ──
      try {
        const mgr = PostFXManager.get(this);
        if (this.bossPhase === 3 && this.bossHP <= 4) {
          // ENRAGED: sustained crimson vignette + heavy shimmer
          mgr?.setDangerHold(0.75);
          mgr?.getPipeline()?.setChromatic(2.0).setGrain(0.06).setVignette(0.55);
        } else if (this.bossPhase === 3) {
          mgr?.setDangerHold(0.45);
          mgr?.getPipeline()?.setChromatic(1.4).setGrain(0.05).setVignette(0.48);
        } else if (this.bossPhase === 2) {
          mgr?.setDangerHold(0.15);
          mgr?.getPipeline()?.setChromatic(0.9).setVignette(0.38);
        } else {
          mgr?.setDangerHold(0);
          mgr?.getPipeline()?.setChromatic(0.6).setVignette(0.32);
        }
        // Punch the transition
        mgr?.pulseDanger(280, this.bossPhase === 3 ? 1.0 : 0.6);
      } catch {}
    }
  }

  update(_time: number, delta: number): void {
    // Post-victory menu navigation (boss already down, scene-level gameplay
    // is frozen). Stays live so D-pad/stick can move the selection halo
    // between MAIN MENU and VIEW SCOREBOARD.
    this.victoryNav?.update();

    if (this.isGameOver || this.isTransitioning) return;

    // Autoplay AI controls player movement
    if (this.isAutoPlay && this.aiPlayer && this.player) {
      this.runAutoplayAI(delta);
    }

    this.updatePlayer(delta);
    this.updateProjectile();
    this.updateBalls(delta);
    this.updateBossAI(delta);
    this.updateStanceOverlay();
    this.checkCollisions();
    this.updateHazards();
    this.updatePlayerHealthHud();

    // === SAFETY: Prevent boss from getting permanently stuck ===
    if (this.boss && this.currentStance !== 'idle' && this.currentStance !== 'death') {
      this.stanceTimer += delta;
      if (this.stanceTimer > this.STANCE_SAFETY_MS) {
        // Force reset to idle — something got stuck
        this.setBossStance('idle');
        this.stanceTimer = 0;
        // Also fix scale/alpha drift
        const safeScale = this.getPhaseScale();
        this.boss.setAlpha(1);
        this.boss.setScale(safeScale);
        if (this.anims.exists('boss_idle')) this.boss.play('boss_idle');
      }
    } else {
      this.stanceTimer = 0;
    }

    // Fix boss alpha drift — if idle, boss should be visible
    if (this.boss && this.currentStance === 'idle' && this.boss.alpha < 0.5) {
      this.boss.setAlpha(1);
    }

    if (this.playerShadow && this.player) this.playerShadow.setX(this.player.x);
  }

  // ========== ARENA ATMOSPHERE ==========
  /**
   * Install parallax layers for the boss arena (far/mid/near).
   * Each layer uses a different scroll factor so camera shake creates
   * subtle depth illusion.
   */
  private installBossParallax(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const layers = [
      { suffix: 'far',  y: HEIGHT * 0.35, depth: 1, alpha: 0.45, scrollFactor: 0.3 },
      { suffix: 'mid',  y: HEIGHT * 0.6,  depth: 2, alpha: 0.6,  scrollFactor: 0.6 },
      { suffix: 'near', y: HEIGHT * 0.9,  depth: 3, alpha: 0.8,  scrollFactor: 0.85 },
    ];
    for (const layer of layers) {
      const key = `parallax_boss_${layer.suffix}`;
      if (!this.textures.exists(key)) continue;
      const img = this.add.image(WIDTH / 2, layer.y, key);
      img.setDepth(layer.depth);
      img.setAlpha(layer.alpha);
      img.setScrollFactor(layer.scrollFactor);
      img.setOrigin(0.5, 0.5);
    }
  }

  private createArenaAtmosphere(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // Rising toxic bubbles
    this.add.particles(0, 0, 'pixel', {
      x: { min: 100, max: WIDTH - 100 }, y: HEIGHT + 10,
      lifespan: { min: 4000, max: 7000 },
      speedY: { min: -30, max: -60 }, speedX: { min: -10, max: 10 },
      scale: { start: 0.3, end: 0.8 },
      alpha: { start: 0.15, end: 0 },
      tint: [0x44FF44, 0x22AA22, 0x88FF44],
      frequency: 300, blendMode: 'ADD',
    }).setDepth(2);

    // Ground toxic fog
    this.fogGraphics = this.add.graphics();
    this.fogGraphics.setDepth(3).setAlpha(0.4);
    this.drawFog();

    // Fog pulse
    this.tweens.add({
      targets: this.fogGraphics,
      alpha: { from: 0.3, to: 0.5 },
      duration: 2000, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }

  private drawFog(): void {
    if (!this.fogGraphics) return;
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    this.fogGraphics.clear();
    this.fogGraphics.fillGradientStyle(0x22AA22, 0x22AA22, 0x22AA22, 0x22AA22, 0, 0, 0.15, 0.15);
    this.fogGraphics.fillRect(0, HEIGHT - this.fogHeight, WIDTH, this.fogHeight);
  }

  // ========== PLAYER ==========
  private createPlayer(): void {
    const firstFrameKey = `${FRAME_KEYS.PLAYER.IDLE}_1`;
    if (this.textures.exists(firstFrameKey)) {
      const player = this.add.sprite(PLAYER_CONFIG.START_X, PLAYER_CONFIG.START_Y, firstFrameKey);
      this.player = player;
      // Lock display size — idle is 600×760, other poses are 300×380. See GameScene.createPlayer for rationale.
      const dispW = PLAYER_CONFIG.WIDTH * PLAYER_CONFIG.SCALE * 1.2;
      const dispH = PLAYER_CONFIG.HEIGHT * PLAYER_CONFIG.SCALE * 1.2;
      const lockDisplaySize = () => player.setDisplaySize(dispW, dispH);
      lockDisplaySize();
      player.on('animationstart', lockDisplaySize);
      if (this.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) player.play(ANIMATION_KEYS.PLAYER.IDLE);
      player.enableFilters();
      player.filters!.internal.addShadow(0, 3, 0.02, 0.5, 0x000000, 9, 0.8);
    }
    this.playerShadow = this.add.ellipse(PLAYER_CONFIG.START_X, PHYSICS_CONFIG.GROUND_Y - 5, 120, 25, 0x000000, 0.35);
    this.playerShadow.setDepth(-1);
  }

  private setupInput(): void {
    this.cursors = this.input.keyboard?.createCursorKeys();
    this.spaceKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    // USB / Bluetooth gamepad — adopt the first connected pad. Same wiring
    // as GameScene so the boss fight controls match the regular stages.
    if (this.input.gamepad) {
      const adopt = (pad: Phaser.Input.Gamepad.Gamepad) => { this.gamepad = pad; };
      const drop = (pad: Phaser.Input.Gamepad.Gamepad) => {
        if (this.gamepad === pad) this.gamepad = undefined;
      };
      const existing = this.input.gamepad.getPad(0);
      if (existing) adopt(existing);
      this.input.gamepad.on('connected', adopt);
      this.input.gamepad.on('disconnected', drop);
    }
  }

  private updatePlayer(delta: number): void {
    if (!this.player || !this.cursors) return;
    const moveDistance = (PLAYER_CONFIG.SPEED * delta) / 1000;
    let isMoving = false;

    // Merge gamepad axis + D-pad with keyboard (deadzone 0.25 matches GameScene).
    let padLeft = false, padRight = false, padShootHeld = false;
    if (this.gamepad) {
      const ax = this.gamepad.axes.length > 0 ? this.gamepad.axes[0].getValue() : 0;
      padLeft  = this.gamepad.left  || ax < -0.25;
      padRight = this.gamepad.right || ax >  0.25;
      padShootHeld = (this.gamepad.buttons[0]?.pressed ?? false) ||
                     (this.gamepad.buttons[1]?.pressed ?? false);
    }

    const moveLeft  = this.cursors.left.isDown  || padLeft;
    const moveRight = this.cursors.right.isDown || padRight;

    if (moveLeft)  { this.player.x -= moveDistance; this.player.setFlipX(true);  isMoving = true; }
    if (moveRight) { this.player.x += moveDistance; this.player.setFlipX(false); isMoving = true; }

    if (this.playerState !== 'shooting' && this.player.type === 'Sprite') {
      if (isMoving && this.playerState !== 'walking') {
        if (this.anims.exists(ANIMATION_KEYS.PLAYER.WALK)) this.player.play(ANIMATION_KEYS.PLAYER.WALK);
        this.playerState = 'walking';
      } else if (!isMoving && this.playerState !== 'idle') {
        if (this.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) this.player.play(ANIMATION_KEYS.PLAYER.IDLE);
        this.playerState = 'idle';
      }
    }

    const halfWidth = (this.player.width * this.player.scaleX) / 2;
    this.player.x = Phaser.Math.Clamp(this.player.x, PHYSICS_CONFIG.WALL_LEFT + halfWidth, PHYSICS_CONFIG.WALL_RIGHT - halfWidth);

    // Shoot — keyboard JustDown + gamepad rising-edge.
    const padShootEdge = padShootHeld && !this.prevGamepadShoot;
    this.prevGamepadShoot = padShootHeld;

    if (this.spaceKey && Phaser.Input.Keyboard.JustDown(this.spaceKey)) this.shootProjectile();
    if (this.cursors.up && Phaser.Input.Keyboard.JustDown(this.cursors.up)) this.shootProjectile();
    if (padShootEdge) this.shootProjectile();
  }

  // ========== BOSS ==========
  private createBoss(): void {
    // BootScene normally builds boss_idle, but if its loader was force-completed
    // before the frames arrived (poor connection), the anim is missing even
    // after preload() re-loads the frames. Rebuild it from whatever frames are
    // present so the hydra animates instead of freezing on frame 1.
    this.ensureBossIdleAnim();

    // Pick the boss texture. If, after preload's retries, no real frame ever
    // arrived (a genuinely dead connection), fall back to a placeholder so the
    // fight is still PLAYABLE and completable rather than a soft-locked dead
    // arena. createBoss must never leave `this.boss` null.
    const bossTexture = this.textures.exists('boss_frame_1')
      ? 'boss_frame_1'
      : this.ensureBossPlaceholderTexture();

    this.boss = this.add.sprite(this.bossBaseX, this.bossBaseY, bossTexture);
    this.boss.setScale(0.7).setDepth(50);
    if (this.anims.exists('boss_idle')) this.boss.play('boss_idle');
    this.boss.enableFilters();
    this.bossGlow = this.boss.filters!.internal.addGlow(0x44FF44, 3, 0, 1, false, 0.1, 8);
    // Idle float
    this.tweens.add({
      targets: this.boss, y: this.bossBaseY + 15,
      duration: 2000, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }

  /** (Re)build the boss idle animation from whatever boss_frame_* are loaded. */
  private ensureBossIdleAnim(): void {
    if (this.anims.exists('boss_idle')) return;
    const frames: Phaser.Types.Animations.AnimationFrame[] = [];
    for (let i = 1; i <= 41; i++) {
      const key = `boss_frame_${i}`;
      if (this.textures.exists(key)) frames.push({ key });
    }
    if (frames.length > 0) {
      this.anims.create({ key: 'boss_idle', frames, frameRate: 12, repeat: -1 });
    }
  }

  /**
   * Last-resort placeholder so a truly dead connection (no boss frame ever
   * arrives, even after preload's retries) yields a playable, completable fight
   * rather than a soft-locked dead arena. A simple toxic orb the attack and
   * weak-window logic can anchor to.
   */
  private ensureBossPlaceholderTexture(): string {
    const key = 'boss_placeholder';
    if (!this.textures.exists(key)) {
      const g = this.add.graphics();
      g.fillStyle(0x163016, 1);
      g.fillCircle(160, 160, 150);
      g.fillStyle(0x44FF44, 1);
      g.fillCircle(118, 138, 24);
      g.fillCircle(202, 138, 24);
      g.generateTexture(key, 320, 320);
      g.destroy();
    }
    console.warn('⚠️ BossScene: no boss frames available — using placeholder so the fight stays playable');
    return key;
  }

  private updateBossAI(delta: number): void {
    if (!this.boss || !this.player || this.isGameOver) return;
    const dt = delta / 1000;

    // Boss tracks player horizontally (slowly)
    const trackSpeed = this.bossPhase === 1 ? 40 : this.bossPhase === 2 ? 80 : 150;
    this.bossTargetX = this.player.x;
    const dx = this.bossTargetX - this.boss.x;
    this.boss.x += Math.sign(dx) * Math.min(Math.abs(dx), trackSpeed * dt);

    // Clamp boss position
    this.boss.x = Phaser.Math.Clamp(this.boss.x, 300, GAME_CONFIG.WIDTH - 300);

    // Phase 3: erratic side-to-side
    if (this.bossPhase === 3) {
      this.boss.x += Math.sin(Date.now() / 300) * 2;
    }
  }

  // ========== BOSS ATTACKS ==========
  private startBossAttacks(): void {
    if (!this.boss) return;
    // Enrage at HP<=4 in phase 3 (scaled to 18 HP cap) speeds attacks up.
    const enraged = this.bossPhase === 3 && this.bossHP <= 4;
    // Exhibition tuning: attack intervals lengthened so first-time players
    // have time to read each telegraph and shoot back between attacks.
    let interval = this.bossPhase === 1 ? 6000 : this.bossPhase === 2 ? 5000 : 4200;
    if (enraged) interval = 3500;

    this.bossAttackTimer = this.time.addEvent({
      delay: interval,
      loop: true,
      callback: () => {
        if (this.isGameOver || !this.boss || this.isTransitioning) return;

        // Choose attack based on phase — more variety per phase
        const attacks = this.bossPhase === 1
          ? ['toxic_spit', 'toxic_spit', 'pollution_rain']
          : this.bossPhase === 2
            ? ['toxic_spit', 'pollution_rain', 'sludge_wave', 'tentacle_sweep']
            : ['toxic_spit', 'pollution_rain', 'sludge_wave', 'tentacle_sweep', 'sludge_wave'];

        const attack = attacks[Math.floor(Math.random() * attacks.length)];

        // Boss is invincible during attack windup
        this.bossInvincible = true;
        this.closeWeakWindow(false);

        switch (attack) {
          case 'toxic_spit': this.attackToxicSpit(); break;
          case 'pollution_rain': this.attackPollutionRain(); break;
          case 'sludge_wave': this.attackSludgeWave(); break;
          case 'tentacle_sweep': this.attackTentacleSweep(); break;
        }
      },
    });
  }

  /** ATTACK 1: Toxic Spit — single ball with anticipation + charge + recovery */
  private attackToxicSpit(): void {
    if (!this.boss) return;

    // === ANTICIPATION: Boss pulses green for 0.5s before attack ===
    // Use absolute scale targets to prevent compounding drift
    const baseScale = this.getPhaseScale();
    this.tweens.add({
      targets: this.boss,
      scaleX: baseScale * 1.08,
      scaleY: baseScale * 1.08,
      duration: 250,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    // Glow buildup on boss
    const chargeGlow = this.add.circle(this.boss.x, this.boss.y + 50, 10, 0x88FF44, 0);
    chargeGlow.setBlendMode(Phaser.BlendModes.ADD).setDepth(55);
    this.tweens.add({
      targets: chargeGlow, scale: 4, alpha: 0.6, duration: 500, ease: 'Power2',
    });

    // After buildup, swap to attack stance + fire
    this.time.delayedCall(500, () => {
      this.setBossStance('attack');
      chargeGlow.destroy();

      // Spit flash — escalates into a spread pattern on later phases.
      if (this.boss) {
        // Exhibition tuning: fewer toxic spit projectiles per phase.
        const spitCount = this.bossPhase === 1 ? 1 : this.bossPhase === 2 ? 1 : 2;
        for (let i = 0; i < spitCount; i++) {
          const spread = spitCount === 1 ? 0 : (i - (spitCount - 1) / 2) * 120;
          this.spawnBossBall(this.boss.x + spread, this.boss.y + 80, this.bossPhase >= 3 ? 48 : 55);
          const ball = this.balls[this.balls.length - 1];
          if (ball) {
            ball.setData('velocityX', spread * 1.4);
            ball.setData('velocityY', 120 + Math.abs(spread) * 0.2);
          }
        }
      }
      if (screenShakeState.isEnabled()) this.cameras.main.shake(80, 0.002);

      // === RECOVERY: Hold attack pose, then expose the core briefly ===
      this.time.delayedCall(400, () => {
        this.setBossStance('idle');
        this.openWeakWindow(2200, this.getWeakWindowHitCap(), 'CORE EXPOSED!');
      });
    });
  }

  /** ATTACK 2: Pollution Rain — anticipation → stance → rain → recovery */
  private attackPollutionRain(): void {
    if (!this.boss) return;

    // === ANTICIPATION: Boss rises up, glows brighter (0.6s) ===
    this.tweens.add({
      targets: this.boss, y: this.boss.y - 30, duration: 400, ease: 'Power2',
      yoyo: true, hold: 300,
    });

    // Green screen tint telegraph
    const greenTint = this.add.rectangle(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT, 0x44FF44, 0);
    greenTint.setOrigin(0, 0).setDepth(300);
    this.tweens.add({ targets: greenTint, alpha: 0.1, duration: 600, yoyo: true, onComplete: () => greenTint.destroy() });

    this.showAttackName('POLLUTION OVERLOAD!');

    // Switch stance after buildup
    this.time.delayedCall(500, () => this.setBossStance('attack'));

    // Rain balls after full telegraph. Exhibition tuning: fewer drops per
    // phase so a player who cannot fully clear the rain still survives it.
    const rainCount = this.bossPhase === 1 ? 4 : this.bossPhase === 2 ? 5 : 7;
    this.time.delayedCall(900, () => {
      for (let i = 0; i < rainCount; i++) {
        this.time.delayedCall(i * 150, () => {
          const laneBias = this.player ? Phaser.Math.Clamp(this.player.x + Phaser.Math.Between(-280, 280), 180, GAME_CONFIG.WIDTH - 180) : GAME_CONFIG.WIDTH / 2;
          const rx = this.bossPhase === 1
            ? 180 + Math.random() * (GAME_CONFIG.WIDTH - 360)
            : Phaser.Math.Clamp(laneBias + Phaser.Math.Between(-220, 220), 160, GAME_CONFIG.WIDTH - 160);
          this.spawnBossBall(rx, 50, 36 + Math.random() * 18);
        });
      }
      this.time.delayedCall(550, () => {
        this.setBossStance('idle');
        this.openWeakWindow(2400, this.getWeakWindowHitCap(), 'OPENING!');
      });
    });
  }

  /** ATTACK 3: Sludge Wave — anticipation → lunge → sweep → recovery */
  private attackSludgeWave(): void {
    if (!this.boss) return;
    const { WIDTH } = GAME_CONFIG;

    this.showAttackName('SLUDGE WAVE!');

    // === AAA: FRAME 1 - WIND-UP (rearing up with energy charging) ===
    const sludgeFrames = ['boss_sludge_1', 'boss_sludge_2', 'boss_sludge_3', 'boss_sludge_4'];
    if (this.textures.exists(sludgeFrames[0])) {
      this.setBossStance('lunge'); // Use lunge as base to create overlay
      if (this.bossStanceOverlay) {
        this.bossStanceOverlay.setTexture(sludgeFrames[0]);
      }
    }

    // Boss rears up — use absolute scale to prevent drift
    const sludgeBaseScale = this.getPhaseScale();
    this.tweens.add({
      targets: this.boss, y: this.bossBaseY - 50, scaleY: sludgeBaseScale * 1.12,
      duration: 500, ease: 'Power2',
    });

    // Ground warning glow (wider, more intense)
    const warnLine = this.add.rectangle(0, PHYSICS_CONFIG.GROUND_Y - 40, WIDTH, 80, 0x44FF44, 0);
    warnLine.setOrigin(0, 0.5).setDepth(4);
    this.tweens.add({ targets: warnLine, alpha: 0.4, duration: 150, yoyo: true, repeat: 4 });

    // Charging energy glow above boss
    const chargeOrb = this.add.circle(this.boss.x, this.boss.y - 80, 15, 0x88FF44, 0);
    chargeOrb.setBlendMode(Phaser.BlendModes.ADD).setDepth(55);
    this.tweens.add({ targets: chargeOrb, scale: 5, alpha: 0.7, duration: 600, ease: 'Power2' });

    // === FRAME 2 - MID-SLAM (600ms) ===
    this.time.delayedCall(600, () => {
      if (this.bossStanceOverlay && this.textures.exists(sludgeFrames[1])) {
        this.bossStanceOverlay.setTexture(sludgeFrames[1]);
      }
      chargeOrb.destroy();

      // Boss SLAMS down hard — absolute scale to prevent drift
      if (this.boss) {
        const slamScale = this.getPhaseScale();
        this.tweens.add({
          targets: this.boss, y: this.bossBaseY + 60, scaleY: slamScale * 0.85,
          duration: 150, ease: 'Bounce.easeOut',
        });
      }

      // Heavy screen shake + green flash
      if (screenShakeState.isEnabled()) this.cameras.main.shake(300, 0.008);
      this.cameras.main.flash(200, 50, 200, 50);

      // Ground shockwave ring
      const shockwave = this.add.circle(this.boss!.x, PHYSICS_CONFIG.GROUND_Y, 30, 0x000000, 0);
      shockwave.setStrokeStyle(4, 0x44FF44, 0.6).setDepth(5);
      this.tweens.add({ targets: shockwave, scaleX: 15, scaleY: 3, alpha: 0, duration: 800, ease: 'Power2', onComplete: () => shockwave.destroy() });
    });

    // === FRAME 3 - WAVE SPREADING (1000ms) ===
    this.time.delayedCall(1000, () => {
      if (this.bossStanceOverlay && this.textures.exists(sludgeFrames[2])) {
        this.bossStanceOverlay.setTexture(sludgeFrames[2]);
      }
      warnLine.destroy();

      // Launch toxic wave balls in both directions, plus a third center lunge in phase 3.
      this.spawnBossBall(this.boss!.x - 100, PHYSICS_CONFIG.GROUND_Y - 60, 45);
      const leftBall = this.balls[this.balls.length - 1];
      if (leftBall) { leftBall.setData('velocityX', -500); leftBall.setData('velocityY', -80); }

      this.spawnBossBall(this.boss!.x + 100, PHYSICS_CONFIG.GROUND_Y - 60, 45);
      const rightBall = this.balls[this.balls.length - 1];
      if (rightBall) { rightBall.setData('velocityX', 500); rightBall.setData('velocityY', -80); }

      if (this.bossPhase >= 3) {
        this.spawnBossBall(this.boss!.x, PHYSICS_CONFIG.GROUND_Y - 100, 40);
        const centerBall = this.balls[this.balls.length - 1];
        if (centerBall) { centerBall.setData('velocityX', 0); centerBall.setData('velocityY', -420); }
      }

      // Toxic ground splash particles
      if (this.popEmitter) {
        this.popEmitter.emitParticleAt(this.boss!.x, PHYSICS_CONFIG.GROUND_Y - 20, 20);
      }
    });

    // === FRAME 4 - RECOVERY (1500ms) ===
    this.time.delayedCall(1500, () => {
      if (this.bossStanceOverlay && this.textures.exists(sludgeFrames[3])) {
        this.bossStanceOverlay.setTexture(sludgeFrames[3]);
      }
    });

    // === RETURN TO IDLE (2200ms) ===
    this.time.delayedCall(2200, () => {
      this.setBossStance('idle');
      // Reset to absolute phase scale — prevents drift from multiplicative tweens
      if (this.boss) {
        const resetScale = this.getPhaseScale();
        this.tweens.add({ targets: this.boss, y: this.bossBaseY, scaleX: resetScale, scaleY: resetScale, duration: 500, ease: 'Sine.easeOut' });
      }
      this.openWeakWindow(2600, this.getWeakWindowHitCap(), 'BREAK THE ARMOR!');
    });
  }

  /** ATTACK 4: Tentacle Sweep — warning zone on ground, then horizontal damage sweep */
  private attackTentacleSweep(): void {
    if (!this.boss) return;
    const { WIDTH } = GAME_CONFIG;

    this.showAttackName('TENTACLE SWEEP!');
    this.setBossStance('lunge');

    // === TELEGRAPH: Red warning zone on ground (1s warning) ===
    const sweepY = PHYSICS_CONFIG.GROUND_Y - 80;
    const warnZone = this.add.rectangle(WIDTH / 2, sweepY, WIDTH - 200, 100, 0xFF4444, 0);
    warnZone.setDepth(4);

    // Flash warning zone
    this.tweens.add({
      targets: warnZone,
      alpha: { from: 0, to: 0.35 },
      duration: 200,
      yoyo: true,
      repeat: 4,
    });

    // Warning text on the zone
    const warnText = this.add.text(WIDTH / 2, sweepY - 60, '⚠ DODGE! ⚠', {
      fontSize: '36px', fontFamily: 'CF Asty Std', color: '#FF4444',
      stroke: '#000000', strokeThickness: 4,
    });
    warnText.setOrigin(0.5).setDepth(10000).setAlpha(0);
    this.tweens.add({ targets: warnText, alpha: 1, duration: 200, yoyo: true, hold: 600, onComplete: () => warnText.destroy() });

    // === SWEEP: After 1s, horizontal tentacle sweeps across ===
    this.time.delayedCall(1000, () => {
      warnZone.destroy();

      // Create the sweep line — a fast-moving green bar
      const sweepStartX = this.boss!.x > WIDTH / 2 ? -100 : WIDTH + 100;
      const sweepLine = this.add.rectangle(
        sweepStartX,
        sweepY, 180, 80, 0x44FF44, 0.7
      );
      sweepLine.setDepth(50);
      sweepLine.setStrokeStyle(3, 0x22CC22, 0.9);

      // Sweep across the screen
      const targetX = this.boss!.x > WIDTH / 2 ? WIDTH + 100 : -100;
      this.tweens.add({
        targets: sweepLine,
        x: targetX,
        duration: 600,
        ease: 'Power1',
        onUpdate: () => {
          // Check if player is in sweep zone
          if (this.player && !this.isInvincible && !designModeState.isDesignMode()) {
            const playerInX = Math.abs(this.player.x - sweepLine.x) < 120;
            const playerInY = this.player.y > sweepY - 80;
            if (playerInX && playerInY) {
              this.playerHit(ENERGY_CONFIG.DAMAGE_BOSS_ATTACK);
            }
          }
        },
        onComplete: () => sweepLine.destroy(),
      });

      // Screen shake on sweep
      if (screenShakeState.isEnabled()) this.cameras.main.shake(150, 0.004);

      // Boss returns to idle after sweep, then briefly exposes the core.
      this.time.delayedCall(800, () => {
        this.setBossStance('idle');
        this.openWeakWindow(2000, this.getWeakWindowHitCap(), 'STRIKE NOW!');
      });

      if (this.bossPhase >= 3) {
        this.time.delayedCall(260, () => {
          const returnSweep = this.add.rectangle(targetX, sweepY, 160, 70, 0x88FF44, 0.55);
          returnSweep.setDepth(49);
          returnSweep.setStrokeStyle(3, 0x22CC22, 0.75);
          this.tweens.add({
            targets: returnSweep,
            x: sweepStartX,
            duration: 420,
            ease: 'Power1',
            onUpdate: () => {
              if (this.player && !this.isInvincible && !designModeState.isDesignMode()) {
                const playerInX = Math.abs(this.player.x - returnSweep.x) < 110;
                const playerInY = this.player.y > sweepY - 80;
                if (playerInX && playerInY) {
                  this.playerHit(ENERGY_CONFIG.DAMAGE_BOSS_ATTACK);
                }
              }
            },
            onComplete: () => returnSweep.destroy(),
          });
        });
      }
    });
  }

  private showAttackName(name: string, color: string = '#88FF44', glow: string = '#004400'): void {
    // AAA: Boss attack SFX
    AudioManager.getInstance().playSFX(this, 'sfx_boss_attack', { volume: 0.6 });

    const text = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2 - 100, name, {
      fontSize: '52px', fontFamily: 'CF Asty Std', color,
      stroke: '#000000', strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 3, color: glow, blur: 10, fill: true },
    });
    text.setOrigin(0.5).setDepth(10000).setScale(0);
    this.tweens.add({ targets: text, scale: 1, alpha: 1, duration: 300, ease: 'Back.easeOut' });
    this.time.delayedCall(1000, () => {
      this.tweens.add({ targets: text, alpha: 0, scale: 1.3, duration: 300, onComplete: () => text.destroy() });
    });
  }

  private bossStanceOverlay?: Phaser.GameObjects.Sprite;
  private currentStance: string = 'idle';
  private readonly BLEND_DURATION = 350; // ms — smooth crossfade time

  /** Switch boss visual stance with buttery smooth crossfade */
  private setBossStance(stance: 'idle' | 'attack' | 'lunge' | 'damaged' | 'death' | 'pain'): void {
    if (!this.boss || stance === this.currentStance) return;
    this.currentStance = stance;

    const textureMap: Record<string, string> = {
      attack: 'boss_attack',
      lunge: 'boss_lunge',
      damaged: 'boss_damaged',
      death: 'boss_death',
      pain: 'boss_pain_1', // Start of pain animation
    };

    // === RETURN TO IDLE ===
    if (stance === 'idle') {
      // Kill any existing stance overlay immediately + start fade
      if (this.bossStanceOverlay) {
        const overlay = this.bossStanceOverlay;
        this.bossStanceOverlay = undefined;
        this.tweens.add({
          targets: overlay,
          alpha: 0,
          duration: this.BLEND_DURATION * 0.7,
          ease: 'Sine.easeIn',
          onComplete: () => { try { overlay.destroy(); } catch {} },
        });
      }

      // IMMEDIATELY restart idle animation — don't wait for blend
      // This prevents the frozen-frame bug where a delayed check skips the restart
      if (this.bossHP <= 7 && this.textures.exists('boss_damaged')) {
        this.boss.stop();
        this.boss.setTexture('boss_damaged');
      } else if (this.anims.exists('boss_idle')) {
        this.boss.play('boss_idle');
      }

      // Restore boss alpha to full visibility
      this.tweens.add({
        targets: this.boss,
        alpha: 1,
        duration: this.BLEND_DURATION,
        ease: 'Sine.easeInOut',
      });

      // Reset stance timer
      this.stanceTimer = 0;
      return;
    }

    // === TRANSITION TO ACTION STANCE ===
    const textureKey = textureMap[stance];
    if (!textureKey || !this.textures.exists(textureKey)) return;

    // Gracefully fade out previous overlay if one exists
    if (this.bossStanceOverlay) {
      const oldOverlay = this.bossStanceOverlay;
      this.tweens.add({
        targets: oldOverlay,
        alpha: 0,
        duration: this.BLEND_DURATION * 0.5, // Fast fade for old overlay
        ease: 'Sine.easeIn',
        onComplete: () => oldOverlay.destroy(),
      });
    }

    // Create new overlay at boss position
    this.bossStanceOverlay = this.add.sprite(this.boss.x, this.boss.y, textureKey);
    this.bossStanceOverlay.setScale(this.boss.scaleX * 0.95, this.boss.scaleY * 0.95); // Start slightly smaller
    this.bossStanceOverlay.setDepth(this.boss.depth + 1);
    this.bossStanceOverlay.setAlpha(0);

    // Copy glow FX from the boss onto the stance overlay
    if (this.bossGlow) {
      const srcColor = (this.bossGlow as unknown as { color: number }).color || 0x44FF44;
      this.bossStanceOverlay.enableFilters();
      this.bossStanceOverlay.filters!.internal.addGlow(
        srcColor,
        this.bossGlow.outerStrength || 3,
        0, 1, false, 0.1, 8,
      );
    }

    // Smooth crossfade: overlay eases in with slight scale-up, boss eases out
    this.tweens.add({
      targets: this.bossStanceOverlay,
      alpha: 1,
      scaleX: this.boss.scaleX,
      scaleY: this.boss.scaleY,
      duration: this.BLEND_DURATION,
      ease: 'Sine.easeInOut',
    });

    this.tweens.add({
      targets: this.boss,
      alpha: 0.15, // Nearly invisible — overlay takes over
      duration: this.BLEND_DURATION,
      ease: 'Sine.easeInOut',
    });
  }

  /** Update stance overlay to perfectly track boss position + float */
  private updateStanceOverlay(): void {
    if (this.bossStanceOverlay && this.boss) {
      // Lerp position for extra smoothness (prevents 1-frame pops)
      this.bossStanceOverlay.x += (this.boss.x - this.bossStanceOverlay.x) * 0.3;
      this.bossStanceOverlay.y += (this.boss.y - this.bossStanceOverlay.y) * 0.3;
    }
    if (this.boss && this.weakSpotGlow && this.weakSpotSpark) {
      const targetY = this.boss.y + 54;
      this.weakSpotGlow.x += (this.boss.x - this.weakSpotGlow.x) * 0.26;
      this.weakSpotGlow.y += (targetY - this.weakSpotGlow.y) * 0.26;
      this.weakSpotSpark.x += (this.boss.x - this.weakSpotSpark.x) * 0.3;
      this.weakSpotSpark.y += (targetY - this.weakSpotSpark.y) * 0.3;
    }
    // Shield follows boss
    if (this.shieldGraphic && this.boss) {
      this.shieldGraphic.x = this.boss.x;
      this.shieldGraphic.y = this.boss.y;
    }
  }

  // ========== BOSS BALL SPAWNING ==========
  private spawnBossBall(x: number, y: number, radius: number): void {
    // Enlarge the boss bubbles a little — scales the body, the inner trash
    // item, the highlight and the hitbox together so visuals match collision.
    radius = radius * 1.15;
    const ball = this.add.sprite(x, y, 'bubble_regular');
    const scale = radius / 128;
    ball.setScale(0).setTint(0x44FF44).setAlpha(0.5).setDepth(50);
    ball.setData('velocityX', (Math.random() - 0.5) * 300);
    ball.setData('velocityY', 80 + Math.random() * 100);
    ball.setData('radius', radius);

    ball.enableFilters();
    ball.filters!.internal.addGlow(0x44FF44, 1.5, 0, 1, false, 0.1, 1.5);

    // Highlight
    const hl = this.add.circle(x, y, radius * 0.2, 0xFFFFFF, 0.3);
    hl.setBlendMode(Phaser.BlendModes.ADD).setDepth(51);
    ball.setData('highlight', hl);

    // Trash item suspended inside the bubble (bottle / can), matching the other
    // stages. The bubble shell is semi-transparent so the item reads as floating
    // inside it. Sits above the bubble fill but below the surface highlight.
    const itemKey = Math.random() < 0.5 ? 'boss_debris_bottle' : 'boss_debris_can';
    if (this.textures.exists(itemKey)) {
      const item = this.add.image(x, y, itemKey).setOrigin(0.5).setDepth(50.5);
      const itemTex = this.textures.get(itemKey).getSourceImage() as HTMLImageElement;
      const targetSize = radius * 1.3; // fits inside the bubble with margin
      const itemScale = Math.min(targetSize / itemTex.width, targetSize / itemTex.height);
      item.setScale(0);
      this.tweens.add({ targets: item, scale: itemScale, duration: 300, ease: 'Back.easeOut' });
      // Slow tumble so the trash drifts in the toxic current.
      this.tweens.add({
        targets: item,
        angle: (Math.random() < 0.5 ? -1 : 1) * 360,
        duration: 11000 + Math.random() * 4000,
        repeat: -1,
        ease: 'Linear',
      });
      ball.setData('itemSprite', item);
    }

    // Spawn animation
    this.tweens.add({ targets: ball, scale, duration: 300, ease: 'Back.easeOut' });

    // Spit flash
    const flash = this.add.circle(x, y, 20, 0x88FF44, 0.8);
    flash.setBlendMode(Phaser.BlendModes.ADD).setDepth(60);
    this.tweens.add({ targets: flash, scale: 3, alpha: 0, duration: 300, onComplete: () => flash.destroy() });

    this.balls.push(ball);
  }

  // ========== BALL PHYSICS ==========
  private updateBalls(delta: number): void {
    const dt = delta / 1000;
    for (let i = this.balls.length - 1; i >= 0; i--) {
      const ball = this.balls[i];
      let vx = ball.getData('velocityX');
      let vy = ball.getData('velocityY');
      const radius = ball.getData('radius');

      vy += PHYSICS_CONFIG.GRAVITY * dt;
      ball.x += vx * dt;
      ball.y += vy * dt;

      if (ball.x - radius < PHYSICS_CONFIG.WALL_LEFT) { ball.x = PHYSICS_CONFIG.WALL_LEFT + radius; vx = Math.abs(vx); }
      if (ball.x + radius > PHYSICS_CONFIG.WALL_RIGHT) { ball.x = PHYSICS_CONFIG.WALL_RIGHT - radius; vx = -Math.abs(vx); }

      if (ball.y + radius > PHYSICS_CONFIG.GROUND_Y) {
        ball.y = PHYSICS_CONFIG.GROUND_Y - radius;
        vy = -Math.abs(vy) * 0.8;
        // Min bounce must clear the player sprite (Y≈876, top≈750).
        // vy=-700 reaches ~y=750 with gravity=980: height = v²/(2g) = 250px
        if (Math.abs(vy) < 700) vy = -700;
      }

      if (ball.y - radius < PHYSICS_CONFIG.CEILING_Y) {
        ball.y = PHYSICS_CONFIG.CEILING_Y + radius;
        vy = Math.abs(vy);
      }

      ball.setData('velocityX', vx);
      ball.setData('velocityY', vy);

      // Toxic drip trail
      if (this.toxicEmitter && Math.random() < 0.25) {
        this.toxicEmitter.emitParticleAt(ball.x, ball.y + radius * 0.5, 1);
      }

      // Update highlight
      const hl = ball.getData('highlight') as Phaser.GameObjects.Arc;
      if (hl?.scene) hl.setPosition(ball.x - radius * 0.2, ball.y - radius * 0.2);

      // Keep the inner trash item glued to the bubble
      const item = ball.getData('itemSprite') as Phaser.GameObjects.Image | undefined;
      if (item?.scene) item.setPosition(ball.x, ball.y);
    }
  }

  // ========== HAZARDS ==========
  private updateHazards(): void {
    if (!this.player || designModeState.isDesignMode()) return;

    // Rising fog in Phase 2+
    if (this.bossPhase >= 2) {
      const maxFog = this.bossPhase === 2 ? 150 : 220;
      if (this.fogHeight < maxFog) {
        this.fogHeight += 0.05;
        this.drawFog();
      }
    }

    // Spawn toxic pools in Phase 2+
    if (this.bossPhase >= 2 && this.toxicPools.length < this.bossPhase && Math.random() < 0.002) {
      this.spawnToxicPool();
    }

    // Check player in toxic pools
    for (const pool of this.toxicPools) {
      if (!this.isInvincible && pool.getData('active')) {
        const dist = Math.abs(this.player.x - pool.x);
        if (dist < pool.width / 2 && this.player.y > PHYSICS_CONFIG.GROUND_Y - 60) {
          this.playerHit(ENERGY_CONFIG.DAMAGE_BOSS_ATTACK);
          break;
        }
      }
    }
  }

  private spawnToxicPool(): void {
    const x = 200 + Math.random() * (GAME_CONFIG.WIDTH - 400);
    const pool = this.add.ellipse(x, PHYSICS_CONFIG.GROUND_Y - 5, 150, 20, 0x44FF44, 0.3);
    pool.setDepth(3).setData('active', true);

    // Appear animation
    pool.setScale(0);
    this.tweens.add({ targets: pool, scale: 1, alpha: 0.4, duration: 500, ease: 'Power2' });

    // Pulsate
    this.tweens.add({
      targets: pool, alpha: { from: 0.25, to: 0.45 }, scaleX: { from: 0.9, to: 1.1 },
      duration: 800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Remove after 8 seconds
    this.time.delayedCall(8000, () => {
      this.tweens.add({
        targets: pool, alpha: 0, scale: 0, duration: 500,
        onComplete: () => {
          const idx = this.toxicPools.indexOf(pool);
          if (idx >= 0) this.toxicPools.splice(idx, 1);
          pool.destroy();
        },
      });
    });

    this.toxicPools.push(pool);
  }

  // ========== COLLISIONS ==========
  private checkCollisions(): void {
    // Projectile vs Boss
    if (this.isProjectileActive && this.boss && this.projectileSprites.length > 0) {
      for (const sprite of this.projectileSprites) {
        const dist = Phaser.Math.Distance.Between(sprite.x, sprite.y, this.boss.x, this.boss.y);
        if (dist < 160) {
          this.hitBoss();
          this.destroyProjectile();
          break;
        }
      }
    }

    // ── TRIPLE-AAA BOSS FIX: PIERCING trident beam ──
    // The trident channels Poseidon's fury — it does NOT stop at the first bubble.
    // Each projectile beam pierces every ball it crosses in a single rise.
    // Without this, the boss frenzy is unwinnable (1 shot = 1 bubble = too slow).
    if (this.isProjectileActive && this.projectileSprites.length > 0) {
      const piercedBalls = (this as any)._piercedBalls as Set<Phaser.GameObjects.Arc> || new Set();
      (this as any)._piercedBalls = piercedBalls;
      for (let i = this.balls.length - 1; i >= 0; i--) {
        const ball = this.balls[i];
        if (piercedBalls.has(ball)) continue; // already destroyed by this beam
        const radius = ball.getData('radius');
        for (const sprite of this.projectileSprites) {
          // Slightly generous radius (was +16 → +24) for the forgiving boss feel
          const dist = Phaser.Math.Distance.Between(sprite.x, sprite.y, ball.x, ball.y);
          if (dist < radius + 24) {
            piercedBalls.add(ball);
            this.destroyBossBall(i);
            // Tiny visual kick — projectile hums through the pop
            const flash = this.add.circle(sprite.x, sprite.y, 22, 0xFFF4C2, 0.9)
              .setBlendMode(Phaser.BlendModes.ADD).setDepth(500);
            this.tweens.add({ targets: flash, scale: 2.4, alpha: 0, duration: 180, onComplete: () => flash.destroy() });
            break; // next ball — keep projectile alive
          }
        }
      }
    }

    // Player vs Balls
    if (this.player && !this.isInvincible && !designModeState.isDesignMode()) {
      const playerRadius = (PLAYER_CONFIG.WIDTH * PLAYER_CONFIG.SCALE * 0.5) / 2;
      for (const ball of this.balls) {
        const dist = Phaser.Math.Distance.Between(this.player.x, this.player.y, ball.x, ball.y);
        if (dist < playerRadius + ball.getData('radius')) {
          this.playerHit();
          break;
        }
      }
    }
  }

  private destroyBossBall(index: number): void {
    const ball = this.balls[index];
    this.score += 100;
    this.syncScore();

    // Pop flash
    const flash = this.add.circle(ball.x, ball.y, 30, 0x88FF44, 0.7);
    flash.setBlendMode(Phaser.BlendModes.ADD).setDepth(999);
    this.tweens.add({ targets: flash, scale: 3, alpha: 0, duration: 250, onComplete: () => flash.destroy() });

    // Pop particles
    if (this.popEmitter) this.popEmitter.emitParticleAt(ball.x, ball.y, 10);

    // Destroy highlight
    const hl = ball.getData('highlight') as Phaser.GameObjects.Arc;
    if (hl) hl.destroy();

    // Pop the inner trash item along with the bubble
    const item = ball.getData('itemSprite') as Phaser.GameObjects.Image | undefined;
    if (item?.scene) {
      this.tweens.add({ targets: item, alpha: 0, scale: 0, duration: 200, onComplete: () => item.destroy() });
    }

    this.tweens.add({
      targets: ball, alpha: 0, scale: 0, duration: 200,
      onComplete: () => ball.destroy(),
    });
    this.balls.splice(index, 1);
  }

  // ========== BOSS DAMAGE & PHASES ==========
  private hitBoss(): void {
    // === INVINCIBILITY CHECK ===
    if (this.bossInvincible || this.shieldActive || !this.weakWindowActive) {
      // Visual feedback that boss is immune
      if (this.boss) {
        this.boss.setTint(0xFFFFFF);
        this.time.delayedCall(100, () => this.boss?.clearTint());
      }
      // Deflect SFX
      AudioManager.getInstance().playSFX(this, 'sfx_boss_hit', { volume: 0.2 });
      return;
    }

    this.bossHP--;
    this.bossHitCount++;
    this.weakWindowHitsRemaining = Math.max(0, this.weakWindowHitsRemaining - 1);
    this.updateHPBar();
    this.score += 500;
    this.syncScore();

    // AAA: Boss hit SFX
    AudioManager.getInstance().playSFX(this, 'sfx_boss_hit', { volume: 0.6 });

    // Phase escalation: later phases can interrupt a punish window with a shield burst.
    const shieldFrequency = 8;
    if (this.bossPhase >= 3 && this.bossHitCount % shieldFrequency === 0 && this.bossHP > 0) {
      this.activateBossShield();
    }

    // === AAA: PAIN ANIMATION SEQUENCE ===
    if (this.boss) {
      // Flash red
      this.boss.setTint(0xFF0000);
      this.time.delayedCall(150, () => this.boss?.clearTint());

      // Shake/recoil
      this.tweens.add({
        targets: this.boss,
        x: this.boss.x + (Math.random() - 0.5) * 50, y: this.boss.y + 20,
        duration: 60, yoyo: true, repeat: 4,
      });

      // Play pain frame sequence (cycle through 4 pain frames)
      this.setBossStance('pain');
      const painFrames = ['boss_pain_1', 'boss_pain_2', 'boss_pain_3', 'boss_pain_4'];
      let frameIndex = 0;
      const painTimer = this.time.addEvent({
        delay: 100, // 100ms per frame
        repeat: 3,
        callback: () => {
          if (this.boss && this.bossStanceOverlay && frameIndex < painFrames.length) {
            const key = painFrames[frameIndex];
            if (this.textures.exists(key)) {
              this.bossStanceOverlay.setTexture(key);
            }
            frameIndex++;
          }
        },
      });

      // Return to idle/damaged after pain animation completes
      this.time.delayedCall(500, () => {
        painTimer.remove();
        if (this.bossHP <= 7) {
          this.setBossStance('damaged');
        } else {
          this.setBossStance('idle');
        }
      });
    }

    // Unified boss hit impact — freeze, shake, shockwave, barrel
    const bossX = this.boss?.x ?? GAME_CONFIG.WIDTH / 2;
    const bossY = this.boss?.y ?? GAME_CONFIG.HEIGHT / 2;
    ImpactSystem.get(this).bossHit(bossX, bossY);

    if (this.weakWindowHitsRemaining > 0 && this.bossHP > 0) {
      this.syncBossHudStatus(`CORE ${this.weakWindowHitsRemaining}`, '#FFEBA6');
    } else if (this.bossHP > 0) {
      this.closeWeakWindow();
    }

    // Phase transitions (18 HP total: phase 2 at 12, phase 3 at 6).
    if (this.bossHP === 12 && this.bossPhase === 1) {
      this.phaseTransition(2, 'THE HYDRA IS ENRAGED!');
    } else if (this.bossHP === 6 && this.bossPhase === 2) {
      this.phaseTransition(3, 'FINAL HEAD!');
    }

    // Enrage check, restart attacks with faster timing
    if (this.bossPhase === 3 && this.bossHP === 2) {
      if (this.bossAttackTimer) this.bossAttackTimer.remove();
      this.startBossAttacks(); // Will pick up enraged timing
      this.showAttackName('ENRAGED!', '#FF6666', '#550000');
      this.syncBossHudStatus('ENRAGED', '#D94F3A');
    }

    // Use damaged stance when low HP (scaled to 18 HP cap).
    if (this.bossHP <= 5) this.setBossStance('damaged');

    // Victory
    if (this.bossHP <= 0) this.bossDefeated();
  }

  /** Activate boss shield — brief invulnerability with visual barrier */
  private activateBossShield(): void {
    if (!this.boss || this.shieldActive) return;
    this.closeWeakWindow(false);
    this.shieldActive = true;
    this.bossInvincible = true;
    this.syncBossHudStatus('SHIELDED', '#7FE572');

    // Create shield visual — glowing barrier circle
    this.shieldGraphic = this.add.circle(this.boss.x, this.boss.y, 160, 0x44FF44, 0.15);
    this.shieldGraphic.setStrokeStyle(4, 0x88FF88, 0.8);
    this.shieldGraphic.setBlendMode(Phaser.BlendModes.ADD);
    this.shieldGraphic.setDepth(55);

    // Shield pulse
    this.tweens.add({
      targets: this.shieldGraphic,
      scale: { from: 0.8, to: 1.1 },
      alpha: { from: 0.15, to: 0.3 },
      duration: 400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Shield dissolves after 1.5s
    this.time.delayedCall(1500, () => {
      if (this.shieldGraphic) {
        this.tweens.add({
          targets: this.shieldGraphic,
          alpha: 0, scale: 1.5,
          duration: 300,
          onComplete: () => { this.shieldGraphic?.destroy(); this.shieldGraphic = undefined; },
        });
      }
      this.shieldActive = false;
      this.bossInvincible = false;
      this.syncBossHudStatus(`PHASE ${this.bossPhase}`, this.bossPhase === 1 ? '#9EE8F4' : this.bossPhase === 2 ? '#F5A623' : '#D94F3A');
    });
  }

  private phaseTransition(newPhase: number, message: string): void {
    this.isTransitioning = true;
    this.bossInvincible = true; // Boss invulnerable during phase transition
    this.closeWeakWindow(false);
    if (this.bossAttackTimer) this.bossAttackTimer.remove();

    // AAA: Phase transition SFX — layered shatter + legacy phase cue
    const audio = AudioManager.getInstance();
    audio.playSFX(this, 'sfx_boss_phase', { volume: 0.6 });
    if (this.cache.audio.exists('sfx_boss_phase_shatter')) {
      audio.playSFX(this, 'sfx_boss_phase_shatter', { volume: 0.85 });
    }

    // Cinematic phase impact — HUGE preset with cosmic time slice
    const bx = this.boss?.x ?? GAME_CONFIG.WIDTH / 2;
    const by = this.boss?.y ?? GAME_CONFIG.HEIGHT / 2;
    ImpactSystem.get(this).bossPhase(bx, by);

    // Zoom to boss
    this.cameras.main.zoomTo(1.15, 600);

    // Phase text
    const text = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2, message, {
      fontSize: '80px', fontFamily: 'CF Asty Std', color: '#FF4444',
      stroke: '#000000', strokeThickness: 8,
      shadow: { offsetX: 0, offsetY: 4, color: '#880000', blur: 15, fill: true },
    });
    text.setOrigin(0.5).setDepth(10000).setScale(0);
    this.tweens.add({ targets: text, scale: 1.1, alpha: 1, duration: 500, ease: 'Back.easeOut' });

    // Boss grows + changes glow color
    if (this.boss) {
      const targetScale = newPhase === 2 ? 0.8 : 0.9;
      this.tweens.add({ targets: this.boss, scale: targetScale, duration: 800 });

      if (this.bossGlow) {
        this.bossGlow.outerStrength = newPhase === 3 ? 8 : 5;
        (this.bossGlow as unknown as { color: number }).color = newPhase === 3 ? 0xFF0000 : 0xFFAA00;
      }
    }

    // Resume after 2 seconds
    this.time.delayedCall(600, () => { // Note: time is slowed so this is ~2 real seconds
      this.time.timeScale = 1;
      this.cameras.main.zoomTo(1, 400);
      this.tweens.add({ targets: text, alpha: 0, scale: 1.5, duration: 300, onComplete: () => text.destroy() });
      this.bossPhase = newPhase;
      this.isTransitioning = false;
      this.bossInvincible = false; // Vulnerable again after transition
      this.startBossAttacks();
      this.syncBossHudStatus(`PHASE ${newPhase}`, newPhase === 2 ? '#F5A623' : '#D94F3A');

      // AAA: Update phase indicator text
      this.children.list.forEach((child: any) => {
        if (child.getData?.('isPhaseText')) {
          const phaseColors: Record<number, string> = { 1: '#88FF88', 2: '#FFAA44', 3: '#FF4444' };
          child.setText(`PHASE ${newPhase}`);
          child.setColor(phaseColors[newPhase] || '#FFFFFF');
          // Pop animation
          this.tweens.add({ targets: child, scale: { from: 1.5, to: 1 }, duration: 300, ease: 'Back.easeOut' });
        }
      });
    });
  }

  // ========== EPIC DEATH SEQUENCE ==========
  // Track stats for outro
  private bossStartTime: number = 0;

  // ========== PLAYER ENERGY HUD (drawn in this scene, top-left) ==========
  // The shared UIScene HUD renders behind the full-screen arena here, so we
  // redraw the same LIVES + ENERGY rows on top of the arena. Visuals match
  // UIScene.redrawEnergyBar / repaintLifeStar so the boss reads like any stage.
  private buildPlayerHealthHud(): void {
    const D = 9500;
    const baseX = this.PLAYER_HP_X;
    const row1Y = this.PLAYER_HP_Y + 12; // LIVES row (center)
    const row2Y = row1Y + 38;            // ENERGY row (center)

    const labelStyle = {
      fontFamily: FONTS.displayLight, fontSize: '20px', color: '#FFFFFF',
      stroke: '#0C1A2E', strokeThickness: 4, letterSpacing: 1,
      shadow: { offsetX: 0, offsetY: 2, color: '#0C1A2E', blur: 0, fill: true },
    } as any;

    const energyLabelW = 84;
    const labelGap = 14;
    const barX = baseX + energyLabelW + labelGap;
    const barW = this.PLAYER_HP_BAR_W;
    const barH = this.PLAYER_HP_BAR_H;
    this.energyBarCx = barX;
    this.energyBarCy = row2Y;

    // LIVES row — label + green stars aligned to the bar start below.
    this.livesLabel = this.add.text(baseX, row1Y, 'LIVES', labelStyle)
      .setOrigin(0, 0.5).setDepth(D).setScrollFactor(0);
    const starSize = 26, starGap = 12;
    this.lifeStars = [];
    for (let i = 0; i < LIVES_CONFIG.MAX_LIVES; i++) {
      const star = this.add.graphics().setDepth(D + 1).setScrollFactor(0);
      star.setPosition(barX + starSize / 2 + i * (starSize + starGap), row1Y);
      star.setData('starSize', starSize);
      this.repaintLifeStar(star, i < this.lives, starSize);
      this.lifeStars.push(star);
    }

    // ENERGY row — label + pill trough (drawn once) + fill/glow (per frame).
    this.playerHpLabel = this.add.text(baseX, row2Y, 'ENERGY', labelStyle)
      .setOrigin(0, 0.5).setDepth(D).setScrollFactor(0);

    const top = row2Y - barH / 2;
    const cornerR = barH / 2;
    this.playerHpBg = this.add.graphics().setDepth(D).setScrollFactor(0);
    this.playerHpBg.fillStyle(HUD_NAVY_DEEP, 1);
    this.playerHpBg.fillRoundedRect(barX, top, barW, barH, cornerR);
    this.playerHpBg.lineStyle(4, HUD_NAVY, 1);
    this.playerHpBg.strokeRoundedRect(barX, top, barW, barH, cornerR);
    this.playerHpGlow = this.add.graphics().setDepth(D).setScrollFactor(0)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.playerHpFill = this.add.graphics().setDepth(D + 1).setScrollFactor(0);

    this.updatePlayerHealthHud();
  }

  private updatePlayerHealthHud(): void {
    if (this.isGameOver || !this.playerHpFill || !this.playerHpGlow) return;
    // Bar reflects current-life energy, read from the UIScene tracker so the
    // color thresholds match the normal-stage energy bar exactly.
    const ui = this.scene.get(SCENE_KEYS.UI) as any;
    const energy = (ui && typeof ui.getEnergy === 'function') ? ui.getEnergy() : ENERGY_CONFIG.MAX_ENERGY;
    const ratio = ENERGY_CONFIG.MAX_ENERGY > 0 ? Math.max(0, energy / ENERGY_CONFIG.MAX_ENERGY) : 0;

    const x = this.energyBarCx;
    const top = this.energyBarCy - this.PLAYER_HP_BAR_H / 2;
    const w = this.PLAYER_HP_BAR_W;
    const h = this.PLAYER_HP_BAR_H;
    const fill = this.playerHpFill, glow = this.playerHpGlow;
    fill.clear();
    glow.clear();
    if (ratio <= 0) return;

    const isDanger = energy <= ENERGY_CONFIG.LOW_ENERGY_THRESHOLD;
    const isCrit = energy <= 1;
    const fillColor = isCrit ? COLORS.dangerRed : isDanger ? COLORS.warningAmber : HUD_GREEN;
    const sheenColor = isCrit ? 0xFFE0E0 : isDanger ? 0xFFF4C2 : HUD_GREEN_LIGHT;

    const innerPad = 3;
    const innerX = x + innerPad;
    const innerTop = top + innerPad;
    const innerH = h - innerPad * 2;
    const innerW = (w - innerPad * 2) * ratio;
    const innerR = innerH / 2;
    const drawW = Math.max(innerH, innerW);

    fill.fillStyle(fillColor, 1);
    fill.fillRoundedRect(innerX, innerTop, drawW, innerH, innerR);
    fill.fillStyle(sheenColor, 0.85);
    fill.fillRoundedRect(innerX + 4, innerTop + 2, Math.max(0, drawW - 8), Math.max(2, innerH * 0.32), innerR);
    glow.fillStyle(fillColor, 0.45);
    glow.fillRoundedRect(innerX - 2, innerTop - 2, drawW + 4, innerH + 4, innerR + 1);
  }

  /** Repaint the boss-HUD life stars to reflect the current `lives` count. */
  private updateLifeStarsHud(): void {
    this.lifeStars.forEach((star, i) => this.repaintLifeStar(star, i < this.lives));
  }

  /** Mirror of UIScene.repaintLifeStar — ALIVE (green) / EMPTY (navy) star. */
  private repaintLifeStar(g: Phaser.GameObjects.Graphics, alive: boolean, size?: number): void {
    const px = (size ?? (g.getData('starSize') as number)) || 30;
    g.setData('starSize', px);
    g.clear();

    const points = 5;
    const outerR = px / 2;
    const innerR = outerR * 0.42;
    const step = Math.PI / points;

    const traceStar = (radiusOffset: number) => {
      g.beginPath();
      for (let i = 0; i < 2 * points; i++) {
        const r = (i % 2 === 0 ? outerR : innerR) + radiusOffset;
        const angle = i * step - Math.PI / 2;
        const x = r * Math.cos(angle);
        const y = r * Math.sin(angle);
        if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
      }
      g.closePath();
    };

    if (alive) {
      g.fillStyle(0x000000, 0.45);
      g.translateCanvas(0, 3);
      traceStar(0);
      g.fillPath();
      g.translateCanvas(0, -3);
      g.fillStyle(HUD_GREEN, 1);
      traceStar(0);
      g.fillPath();
      g.fillStyle(HUD_GREEN_LIGHT, 0.65);
      traceStar(-3);
      g.fillPath();
      g.lineStyle(3, HUD_NAVY, 1);
      traceStar(0);
      g.strokePath();
    } else {
      g.fillStyle(HUD_NAVY_DEEP, 0.9);
      traceStar(0);
      g.fillPath();
      g.lineStyle(3, HUD_NAVY, 1);
      traceStar(0);
      g.strokePath();
    }
  }

  private hidePlayerHealthHud(): void {
    this.playerHpBg?.setVisible(false);
    this.playerHpFill?.setVisible(false);
    this.playerHpGlow?.setVisible(false);
    this.playerHpLabel?.setVisible(false);
    this.livesLabel?.setVisible(false);
    this.lifeStars.forEach((s) => s.setVisible(false));
  }

  private bossDefeated(): void {
    this.isGameOver = true;
    this.hidePlayerHealthHud();
    // The shared UIScene HUD stayed hidden behind the arena during the fight;
    // stop it so nothing lingers into the victory screen.
    try { this.scene.stop(SCENE_KEYS.UI); } catch {}
    if (this.bossAttackTimer) this.bossAttackTimer.remove();
    this.closeWeakWindow(false);

    // AAA: Epic boss death SFX + unified COSMIC impact + kill cam
    const am = AudioManager.getInstance();
    am.playSFX(this, 'sfx_boss_death', { volume: 0.9 });
    // Oceanic Overwatch: Poseidon boss-kill roar on the tail of the crash
    this.time.delayedCall(900, () => am.playVoiceStinger(this, 'boss_kill', 4000));
    const bx = this.boss?.x ?? GAME_CONFIG.WIDTH / 2;
    const by = this.boss?.y ?? GAME_CONFIG.HEIGHT / 2;
    const impact = ImpactSystem.get(this);
    impact.bossDeath(bx, by);
    impact.finalBlow(bx, by, 1400);

    // Destroy all balls
    this.balls.forEach(b => {
      const hl = b.getData('highlight') as Phaser.GameObjects.Arc;
      if (hl) hl.destroy();
      const item = b.getData('itemSprite') as Phaser.GameObjects.Image | undefined;
      if (item?.scene) item.destroy();
      this.tweens.add({ targets: b, alpha: 0, scale: 0, duration: 300, onComplete: () => b.destroy() });
    });
    this.balls = [];

    // Destroy toxic pools
    this.toxicPools.forEach(p => p.destroy());
    this.toxicPools = [];

    if (!this.boss) { this.showVictory(); return; }

    this.score += 5000;
    this.syncScore();

    // Achievement
    const achievements = AchievementManager.getInstance();
    achievements.setScene(this);
    achievements.unlock('boss_defeated');

    // === AAA DEATH SEQUENCE (fully time.delayedCall, robust) ===
    // Hit-freeze briefly via ImpactSystem (already declared above)
    impact.freeze(200);
    impact.setBaseTimeScale(0.25); // Ultra slow-mo baseline for drama

    // Zoom into boss
    this.cameras.main.zoomTo(1.3, 800, 'Power2');

    // Set first death frame immediately via stance overlay
    this.setBossStance('death');

    // Cycle through death dissolve frames using Phaser's timer
    const deathFrames = ['boss_death_anim_1', 'boss_death_anim_2', 'boss_death_anim_3', 'boss_death_anim_4'];
    let deathFrameIndex = 0;
    const dissolveTimer = this.time.addEvent({
      delay: 100, // frames use SCENE time which is slow-mo so real delay is longer
      repeat: deathFrames.length - 1,
      callback: () => {
        if (!this.boss || deathFrameIndex >= deathFrames.length) return;
        const key = deathFrames[deathFrameIndex];
        if (this.textures.exists(key)) {
          if (this.bossStanceOverlay) {
            this.bossStanceOverlay.setTexture(key);
          } else if (this.boss) {
            this.boss.setTexture(key);
          }
        }
        deathFrameIndex++;
        // Particle bursts at each dissolve step
        if (this.popEmitter && this.boss) {
          this.popEmitter.emitParticleAt(
            this.boss.x + (Math.random() - 0.5) * 200,
            this.boss.y + (Math.random() - 0.5) * 150,
            15 + deathFrameIndex * 5,
          );
        }
      },
    });

    // Boss shakes during dissolve
    if (this.boss) {
      this.tweens.add({
        targets: this.boss,
        x: this.boss.x + (Math.random() - 0.5) * 40,
        duration: 50, yoyo: true, repeat: 30,
      });
    }

    // Use WALL-CLOCK setTimeout only for the final explosion trigger
    // (scene time is slow-mo'd so this needs real time to fire reliably)
    window.setTimeout(() => {
      if (this.isGameOver === false || !this.scene?.isActive?.()) return;
      dissolveTimer.remove();

      // Restore time scale
      impact.setBaseTimeScale(1);
      this.time.timeScale = 1;

      this.cameras.main.zoomTo(1, 800, 'Power2');

      // MASSIVE white flash + shake
      this.cameras.main.flash(1200, 255, 255, 255);
      if (screenShakeState.isEnabled()) this.cameras.main.shake(2000, 0.02);

      // Massive multi-burst particle explosion
      if (this.popEmitter && this.boss) {
        for (let i = 0; i < 8; i++) {
          this.time.delayedCall(i * 80, () => {
            if (this.popEmitter && this.boss) {
              this.popEmitter.emitParticleAt(
                this.boss.x + (Math.random() - 0.5) * 300,
                this.boss.y + (Math.random() - 0.5) * 200,
                25,
              );
            }
          });
        }
      }

      // Boss dissolves completely, then trigger ocean restoration
      if (this.boss) {
        const bossRef = this.boss;
        this.tweens.add({
          targets: bossRef,
          scale: 0.05, alpha: 0, angle: 90,
          duration: 2000, ease: 'Power3',
          onComplete: () => {
            try { bossRef.destroy(); } catch {}
            this.boss = undefined;
            if (this.bossStanceOverlay) {
              try { this.bossStanceOverlay.destroy(); } catch {}
              this.bossStanceOverlay = undefined;
            }
            this.oceanRestoration();
          },
        });
        if (this.bossStanceOverlay) {
          this.tweens.add({
            targets: this.bossStanceOverlay,
            scale: 0.05, alpha: 0, angle: 90,
            duration: 2000, ease: 'Power3',
          });
        }
      } else {
        // Boss already gone — go straight to restoration
        this.oceanRestoration();
      }
    }, 1200); // 1.2s real-time before final explosion

    // SAFETY NET: if the above chain fails for any reason, force-show
    // victory after 15 real seconds so the player is never stuck.
    window.setTimeout(() => {
      if (this.scene?.isActive?.() && !this._victoryShown) {
        console.warn('⚠️ Boss victory safety net triggered');
        this._victoryShown = true;
        try { this.showVictory(); } catch (e) { console.error('showVictory failed:', e); }
      }
    }, 15000);
  }

  // Track whether victory has been shown to avoid duplicate calls
  private _victoryShown = false;

  private oceanRestoration(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // Ensure time is flowing
    this.time.timeScale = 1;
    try { ImpactSystem.get(this).setBaseTimeScale(1); } catch {}

    // Fade out toxic fog
    if (this.fogGraphics) {
      this.tweens.add({ targets: this.fogGraphics, alpha: 0, duration: 3000 });
    }

    // Blue ocean flash — the pollution is cleansed
    this.cameras.main.flash(1500, 50, 150, 255);

    // ── TRIPLE-AAA: CROSSFADE polluted Hydra arena → cleansed World 1 ocean ──
    // Narrative beat: Poseidon's victory has purified the water. Slow 3.5s
    // dissolve from the toxic hydra-stage video to the exact video used in
    // Stage 1 "Calm Waters" (Coral Reef) — visually closes the loop.
    if (this.backgroundManager) {
      this.backgroundManager.crossfadeTo({
        type: 'video',
        video: 'world1_video', // matches bg_video_world1_video (game-content/world1 video.mp4)
        scaleMode: 'cover',
        opacity: 1.0,
      }, 3500).catch((err) => {
        console.warn('⚠️ Ocean-cleanse crossfade failed:', err);
      });
    }

    // Lift PostFX danger channel + lighten the cinematic grade as part of cleansing
    try {
      const mgr = PostFXManager.get(this);
      mgr?.setDangerHold(0);
      // Smoothly transition from the heavy "boss" preset to the lighter "gameplay" preset
      this.tweens.addCounter({
        from: 0,
        to: 1,
        duration: 3500,
        onUpdate: (tw) => {
          const t = tw.getValue() ?? 0;
          const pipe = mgr?.getPipeline();
          if (!pipe) return;
          // boss → gameplay: chromatic 1.2 → 0.6, vignette 0.45 → 0.32, bloom 0.75 → 0.55
          pipe.setChromatic(1.2 - 0.6 * t);
          pipe.setVignette(0.45 - 0.13 * t);
          pipe.setBloom(0.75 - 0.2 * t, 0.6 + 0.08 * t);
          pipe.setGrain(0.05 - 0.02 * t);
        },
      });
    } catch {}

    // Cinematic text: "THE POLLUTION... IS DEFEATED"
    const purifyText = this.add.text(WIDTH / 2, HEIGHT / 2, 'THE POLLUTION... IS DEFEATED', {
      fontSize: '64px', fontFamily: 'CF Asty Std', color: '#88DDFF',
      stroke: '#001133', strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 0, color: '#00AAFF', blur: 20, fill: false },
    });
    purifyText.setOrigin(0.5).setDepth(10000).setAlpha(0);
    this.tweens.add({ targets: purifyText, alpha: 1, duration: 1000, delay: 500 });
    this.tweens.add({ targets: purifyText, alpha: 0, y: HEIGHT / 2 - 40, duration: 800, delay: 3000, onComplete: () => purifyText.destroy() });

    // Clean ocean particles (blue/cyan instead of toxic green)
    this.add.particles(0, 0, 'pixel', {
      x: { min: 0, max: WIDTH }, y: HEIGHT + 10,
      lifespan: { min: 3000, max: 6000 },
      speedY: { min: -40, max: -20 },
      speedX: { min: -10, max: 10 },
      scale: { start: 0.3, end: 0.6 },
      alpha: { start: 0.3, end: 0 },
      tint: [0x4dd0e1, 0x88EEFF, 0x66CCFF, 0xAADDFF],
      frequency: 80, blendMode: 'ADD',
    }).setDepth(9990);

    this.time.delayedCall(4000, () => this.showVictory());
  }

  private showVictory(): void {
    if (this._victoryShown) return;
    this._victoryShown = true;
    // Victory card is a static, abandonable screen: let the idle watchdog reset
    // the kiosk if nobody acts on it for 40s. Cleared on scene shutdown.
    KioskWatchdog.instance.setBossVictory(true);
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // Ensure time is flowing (in case slow-mo got stuck)
    this.time.timeScale = 1;

    // Defensive: tear down any pre-boss / bridge NarrativeCardModal still
    // attached (e.g. if the 15s safety net fires while a modal lingers from
    // a paused intro) so the victory layout never collides with the intro.
    this.children.list
      .filter((o: any) => o instanceof NarrativeCardModal)
      .forEach((m: any) => { try { m.destroy?.(); } catch {} });

    // Clear any lingering pollution bubbles (+ their items/highlights) and stop
    // spawns so NONE appear in the victory background, regardless of which path
    // reached victory (boss death or the safety net).
    if (this.bossAttackTimer) this.bossAttackTimer.remove();
    this.balls.forEach(b => {
      const item = b.getData('itemSprite') as Phaser.GameObjects.Image | undefined;
      if (item?.scene) item.destroy();
      const hl = b.getData('highlight') as Phaser.GameObjects.Arc | undefined;
      if (hl?.scene) hl.destroy();
      if (b.scene) b.destroy();
    });
    this.balls = [];

    // === AAA VICTORY OUTRO WITH FULL STATS ===

    // Dark cinematic overlay at depth 9998
    const overlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x000000, 0);
    overlay.setOrigin(0, 0).setDepth(9998);
    this.tweens.add({ targets: overlay, fillAlpha: 0.7, duration: 800 });

    // ─── HERO POSEIDON ON THE RIGHT, BIG ───────────────────────────────
    // Stats panel sits in the left-center; Poseidon stands hero-pose on the
    // right side as the dominant figure. The gameplay sprite is retired here
    // and replaced by the dedicated victory clip — he raises the V sign once
    // and holds the final pose (bg-removed from the client render).
    const poseidonX = WIDTH * 0.80;
    const poseidonY = HEIGHT * 0.52; // elevated a little
    const HERO_H = 648; // 10% smaller (was 720)

    // Retire the gameplay Poseidon — the victory clip stands in his place.
    try { this.player?.setVisible(false); this.player?.setActive(false); } catch {}

    if (this.textures.exists('poseidon_victory_clip_1')) {
      const hero = this.add.sprite(poseidonX, poseidonY, 'poseidon_victory_clip_1');
      hero.setDepth(9999); // Above overlay
      hero.setDisplaySize((hero.width * HERO_H) / hero.height, HERO_H);

      // Snap-in reveal, then play the V-sign clip once (holds final pose).
      const baseSX = hero.scaleX;
      const baseSY = hero.scaleY;
      hero.setScale(baseSX * 0.92, baseSY * 0.92).setAlpha(0);
      this.tweens.add({
        targets: hero,
        alpha: 1,
        scaleX: baseSX,
        scaleY: baseSY,
        duration: 420,
        ease: 'Back.easeOut',
        onComplete: () => {
          if (this.anims.exists(ANIMATION_KEYS.PLAYER.VICTORY_CLIP)) {
            try { hero.play(ANIMATION_KEYS.PLAYER.VICTORY_CLIP); } catch {}
          }
        },
      });

      // (Golden glow behind the hero removed per client request.)
    }

    // ElevenLabs Clyde TTS: "THE OCEAN IS SAVED!" — lands on his triumphant pose.
    this.time.delayedCall(900, () => {
      try {
        if (this.cache.audio.exists('voice_ocean_saved')) {
          AudioManager.getInstance().playSFX(this, 'voice_ocean_saved', { volume: 0.9 });
        } else if (this.cache.audio.exists('voice_victory')) {
          AudioManager.getInstance().playSFX(this, 'voice_victory', { volume: 0.8 });
        }
      } catch {}
    });

    // Halo of gold particles radiating from Poseidon's position
    try {
      this.add.particles(poseidonX, poseidonY, 'pixel', {
        speed: { min: 100, max: 250 },
        angle: { min: 0, max: 360 },
        scale: { start: 0.6, end: 0 },
        alpha: { start: 0.8, end: 0 },
        lifespan: 1500,
        tint: [0xFFD700, 0xFFAA00, 0xFFFF44, 0xFFCC00],
        frequency: 50,
        blendMode: 'ADD',
      }).setDepth(9997);
    } catch {}

    // Golden particle rain
    this.add.particles(0, 0, 'pixel', {
      x: { min: 0, max: WIDTH }, y: -20,
      lifespan: { min: 4000, max: 7000 },
      speedY: { min: 40, max: 80 },
      speedX: { min: -15, max: 15 },
      scale: { start: 0.4, end: 0 },
      alpha: { start: 0.7, end: 0 },
      tint: [0xFFD700, 0xFFAA00, 0xFFFF44, 0xFFCC00],
      frequency: 30, blendMode: 'ADD',
    }).setDepth(9999);

    // ===== STAGGERED DRAMATIC ENTRANCE =====
    let delay = 0;

    // "VICTORY" title
    const victoryText = this.add.text(WIDTH / 2, 120, 'VICTORY', {
      fontSize: '140px', fontFamily: 'Skranji', color: '#FFD700',
      stroke: '#000000', strokeThickness: 12,
      shadow: { offsetX: 0, offsetY: 8, color: '#000000', blur: 30, fill: true },
    });
    victoryText.setOrigin(0.5).setDepth(10000).setScale(0);
    delay += 200;
    this.time.delayedCall(delay, () => {
      this.tweens.add({ targets: victoryText, scale: 1, duration: 800, ease: 'Back.easeOut', easeParams: [2] });
    });

    // Subtitle
    delay += 600;
    const subText = this.add.text(WIDTH / 2, 210, 'THE OCEAN IS SAVED!', {
      fontSize: '48px', fontFamily: 'CF Asty Std', color: '#4dd0e1',
      stroke: '#000000', strokeThickness: 5,
    });
    subText.setOrigin(0.5).setDepth(10000).setAlpha(0);
    this.time.delayedCall(delay, () => {
      this.tweens.add({ targets: subText, alpha: 1, duration: 500 });
    });

    // === STATS PANEL ===
    delay += 500;
    const panelX = WIDTH / 2;
    const panelY = 350;

    // Stats background
    const statsBg = this.add.rectangle(panelX, panelY + 65, 700, 310, 0x0a1a2a, 0.85);
    statsBg.setStrokeStyle(2, 0x4dd0e1, 0.5);
    statsBg.setDepth(10000).setAlpha(0);
    this.time.delayedCall(delay, () => {
      this.tweens.add({ targets: statsBg, alpha: 1, duration: 400 });
    });

    // Calculate stats
    const timeFought = Math.round((this.time.now - (this.bossStartTime || this.time.now - 60000)) / 1000);
    const livesLeft = this.lives;
    const finalScore = this.score;

    // Stats entries (staggered)
    const stats = [
      { label: 'FINAL SCORE', value: finalScore.toLocaleString(), color: '#FFFFFF' },
      { label: 'LIVES REMAINING', value: `${livesLeft} / ${LIVES_CONFIG.MAX_LIVES}`, color: livesLeft >= 2 ? '#00FF88' : '#FF6644' },
      { label: 'TIME FOUGHT', value: `${timeFought}s`, color: '#AADDFF' },
      { label: 'BOSS HITS DEALT', value: `${this.maxBossHP}`, color: '#FFD700' },
    ];

    stats.forEach((stat, i) => {
      const sy = panelY - 10 + i * 55;
      delay += 150;

      const label = this.add.text(panelX - 280, sy, stat.label, {
        fontSize: '36px', fontFamily: 'CF Asty Std', color: '#88AACC',
        stroke: '#000000', strokeThickness: 2,
      });
      label.setDepth(10001).setAlpha(0);

      const value = this.add.text(panelX + 280, sy, stat.value, {
        fontSize: '40px', fontFamily: 'CF Asty Std', color: stat.color,
        stroke: '#000000', strokeThickness: 3,
      });
      value.setOrigin(1, 0).setDepth(10001).setAlpha(0);

      this.time.delayedCall(delay, () => {
        this.tweens.add({ targets: [label, value], alpha: 1, duration: 300, ease: 'Power2' });
      });
    });

    // === ENVIRONMENTAL MESSAGE ===
    delay += 1200;
    const envMessage = this.add.text(panelX, HEIGHT - 52,
      'You helped save the seas. In the real world, avoid single-use plastics to protect our ocean.', {
      fontSize: '22px', fontFamily: 'CF Asty Std', color: '#66AACC',
      stroke: '#001122', strokeThickness: 2,
      wordWrap: { width: 820 }, align: 'center',
    });
    envMessage.setOrigin(0.5).setDepth(10001).setAlpha(0);
    this.time.delayedCall(delay, () => {
      this.tweens.add({ targets: envMessage, alpha: 0.8, duration: 600 });
    });

    // Submit score
    this.submitBossScore();

    // ── AUTOPLAY: loop back to stage 1 automatically ───────────────────
    if (this.isAutoPlay) {
      this.time.delayedCall(6000, () => {
        this.cameras.main.fadeOut(1500, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.stop(SCENE_KEYS.UI);
          console.log('🤖 AUTO-PLAY: Boss defeated → looping back to stage 1');
          this.scene.start(SCENE_KEYS.GAME, { stage: 1, score: 0, autoPlay: true });
        });
      });
      return;
    }

    // ── PLAYER CHOICE: MAIN MENU vs VIEW SCOREBOARD ────────────────────
    // Appear after the stats so the player reads the score first, then
    // chooses where to go. No auto-return — they decide.
    delay += 1800;
    this.time.delayedCall(delay, () => this.showVictoryMenuButtons(panelX, 700));

    // Bottom-right CREDITS link, mirroring the game-over card placement.
    // Same silhouette as the home-screen SETTINGS label: subtle white text
    // with hover bloom, no pill chrome. Sits below Poseidon's feet so the
    // hero pose stays clean.
    const creditsLink = createSubtleLink(this, {
      x: WIDTH - 130,
      y: 985,
      label: 'CREDITS',
      onClick: () => {
        this.cameras.main.fadeOut(500, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          try { this.scene.stop(SCENE_KEYS.UI); } catch {}
          try { transitionTo(this, SCENE_KEYS.CREDITS); } catch { this.scene.start(SCENE_KEYS.CREDITS); }
        });
      },
    });
    creditsLink.setDepth(10002).setAlpha(0);
    this.tweens.add({ targets: creditsLink, alpha: 1, duration: 380, delay: 300, ease: 'Power2' });
    // Expose it so the victory-menu nav (built later in showVictoryMenuButtons)
    // can include it as a focusable target.
    this.victoryCreditsLink = creditsLink;
  }

  /**
   * Post-victory menu — two oceanic nav buttons stacked below the stats
   * panel. Player picks their next destination; no auto-timeout.
   */
  private showVictoryMenuButtons(centerX: number, centerY: number): void {
    const buttonSfx = () => {
      try { AudioManager.getInstance().playSFX(this, 'sfx_btn_click_heavy', { volume: 0.6 }); } catch {}
    };

    // MAIN MENU — primary action, goes back to title screen
    const mainMenuBtn = createPillButton(this, {
      x: centerX, y: centerY,
      label: 'MAIN MENU',
      variant: 'primary',
      size: 'medium',
      width: 480,
      onClick: () => {
        buttonSfx();
        this.cameras.main.fadeOut(700, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          try { this.scene.stop(SCENE_KEYS.UI); } catch {}
          try { transitionTo(this, SCENE_KEYS.MENU); } catch { this.scene.start(SCENE_KEYS.MENU); }
        });
      },
    });
    mainMenuBtn.setDepth(10002).setAlpha(0);

    // VIEW SCOREBOARD — secondary, jumps to leaderboard
    //   (LeaderboardScene already has a BACK TO MENU button built in)
    const scoreboardBtn = createPillButton(this, {
      x: centerX, y: centerY + 92,
      label: 'VIEW SCOREBOARD',
      variant: 'secondary',
      size: 'medium',
      width: 480,
      onClick: () => {
        buttonSfx();
        this.cameras.main.fadeOut(700, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          try { this.scene.stop(SCENE_KEYS.UI); } catch {}
          try { transitionTo(this, SCENE_KEYS.LEADERBOARD); } catch { this.scene.start(SCENE_KEYS.LEADERBOARD); }
        });
      },
    });
    scoreboardBtn.setDepth(10002).setAlpha(0);

    // Staggered entrance
    this.tweens.add({ targets: mainMenuBtn, alpha: 1, duration: 380, ease: 'Power2' });
    this.tweens.add({ targets: scoreboardBtn, alpha: 1, duration: 380, delay: 150, ease: 'Power2' });

    // ── Joystick selection halo — nothing pre-selected (focusIdx = -1) so
    // no frame is visible until the player moves onto a CTA with the
    // joystick / arrow keys / mouse. Both A and B then press the focused
    // button, so a button without focus can't fire.
    //
    // The CREDITS corner link (created in showVictory) is appended as the
    // final focus target so the joystick can reach it too. It uses its own
    // built-in hover halo, toggled here on focus enter / leave.
    const creditsLink = this.victoryCreditsLink;
    const buttons = [mainMenuBtn, scoreboardBtn];
    const creditsIdx = creditsLink ? buttons.length : -1; // -1 ⇒ no credits link
    const navCount = buttons.length + (creditsLink ? 1 : 0);
    let focusIdx = -1;
    const halo = this.add.graphics().setDepth(10001).setAlpha(0);

    const drawHalo = () => {
      if (focusIdx < 0) { halo.clear(); return; }
      // CREDITS is a text link, not a pill — give it the SAME bold focus ring
      // as the CTAs (drawn as a rounded rect) so a joystick / keyboard player
      // can clearly see the selection land on it. Without this the corner link
      // only lit its own faint glow, which reads as "focus disappeared".
      if (creditsLink && focusIdx === creditsIdx) {
        const sx = creditsLink.scaleX, sy = creditsLink.scaleY;
        drawSelectionHalo(halo, {
          cx: creditsLink.x,
          cy: creditsLink.y,
          w: 200 * sx,
          h: 60 * sy,
          rx: 30 * sx,
          ry: 30 * sy,
        }, { gap: 12 });
        return;
      }
      const b = buttons[focusIdx];
      if (!b || !b.active) { halo.clear(); return; }
      drawSelectionHalo(halo, {
        cx: b.x,
        cy: b.y,
        w: ((b as any).pillWidth ?? 480) * b.scaleX,
        h: ((b as any).pillHeight ?? 110) * b.scaleY,
        rx: ((b as any).pillCapRx ?? 55) * b.scaleX,
        ry: ((b as any).pillCapRy ?? 55) * b.scaleY,
      }, { gap: 14 });
    };

    // Move focus and toggle the CREDITS link's built-in halo on enter / leave.
    // The mouse-hover sync listener below never emits, so there is no recursion.
    const setFocus = (next: number) => {
      const wasCredits = focusIdx === creditsIdx;
      const isCredits = next === creditsIdx;
      if (creditsLink && wasCredits && !isCredits) creditsLink.emit('pointerout');
      if (creditsLink && !wasCredits && isCredits) creditsLink.emit('pointerover');
      focusIdx = next;
      drawHalo();
    };

    // Match the staggered button entrance so the ring (once first selected)
    // is already at full alpha and ready to render.
    this.tweens.add({ targets: halo, alpha: 1, duration: 380, ease: 'Power2' });
    this.events.on(Phaser.Scenes.Events.UPDATE, drawHalo);

    const step = (dir: number) => {
      const next = focusIdx < 0
        ? 0
        : Phaser.Math.Clamp(focusIdx + dir, 0, navCount - 1);
      setFocus(next);
    };
    const confirm = () => {
      if (focusIdx < 0) return;
      if (focusIdx === creditsIdx) { creditsLink?.emit('pointerdown'); return; }
      (buttons[focusIdx] as any)?.press?.();
    };

    mainMenuBtn.on('pointerover',   () => setFocus(0));
    scoreboardBtn.on('pointerover', () => setFocus(1));
    // Mouse hover on CREDITS only syncs the focus index (its visible halo comes
    // from the SubtleLink's own pointerover handler) — no emit → no recursion.
    if (creditsLink) creditsLink.on('pointerover', () => { focusIdx = creditsIdx; drawHalo(); });

    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-UP',    () => step(-1));
      kb.on('keydown-DOWN',  () => step(1));
      kb.on('keydown-LEFT',  () => step(-1));
      kb.on('keydown-RIGHT', () => step(1));
      kb.on('keydown-ENTER', confirm);
      kb.on('keydown-SPACE', confirm);
    }

    this.victoryNav = new GamepadNav(this, {
      onUp:      () => step(-1),
      onDown:    () => step(1),
      onLeft:    () => step(-1),
      onRight:   () => step(1),
      onConfirm: confirm,
    });
  }

  // ========== HP BAR ==========
  // AAA HP bar components
  private hpBarFill?: Phaser.GameObjects.Graphics;
  private hpBarDamageFill?: Phaser.GameObjects.Graphics; // Delayed damage (white) that catches up
  private hpBarGlow?: Phaser.GameObjects.Graphics;
  private hpBarFrame?: Phaser.GameObjects.Graphics;
  private hpBarPhaseIcons: Phaser.GameObjects.Text[] = [];
  private hpDisplayedRatio: number = 1; // For smooth damage animation
  private hpDamageRatio: number = 1; // White "delayed damage" bar

  private createHPBar(): void {
    const { WIDTH } = GAME_CONFIG;
    const barW = 600, barH = 30, barX = (WIDTH - barW) / 2, barY = 76;

    // Triple-A: v2 boss HP frame
    const hpFrameKey = this.textures.exists('boss_v2_hp_frame') ? 'boss_v2_hp_frame' : null;
    if (hpFrameKey) {
      const frameImg = this.add.image(barX + barW / 2, barY + barH / 2, hpFrameKey);
      frameImg.setDisplaySize(barW + 120, barH + 60);
      frameImg.setDepth(999);
    }

    // Dark background with border
    this.hpBarBg = this.add.rectangle(barX + barW / 2, barY + barH / 2, barW + 8, barH + 8, 0x111111, 0.9);
    this.hpBarBg.setStrokeStyle(2, 0x333333, 0.8);
    this.hpBarBg.setDepth(1000);

    // Inner bevel effect (dark inner border)
    const innerBevel = this.add.rectangle(barX + barW / 2, barY + barH / 2, barW + 2, barH + 2, 0x000000, 0.5);
    innerBevel.setDepth(1000);

    // Delayed damage fill (white/yellow bar that catches up after hit)
    this.hpBarDamageFill = this.add.graphics();
    this.hpBarDamageFill.setDepth(1001);

    // Main HP fill
    this.hpBarFill = this.add.graphics();
    this.hpBarFill.setDepth(1002);

    // Glow overlay (additive shimmer on top)
    this.hpBarGlow = this.add.graphics();
    this.hpBarGlow.setDepth(1003);
    this.hpBarGlow.setBlendMode(Phaser.BlendModes.ADD);

    // Frame overlay (segment dividers + border)
    this.hpBarFrame = this.add.graphics();
    this.hpBarFrame.setDepth(1004);

    // Draw frame segments
    this.hpBarFrame.lineStyle(1, 0x000000, 0.6);
    for (let i = 1; i < this.maxBossHP; i++) {
      const sx = barX + (barW / this.maxBossHP) * i;
      this.hpBarFrame.lineBetween(sx, barY - 1, sx, barY + barH + 1);
    }
    // Outer border
    this.hpBarFrame.lineStyle(2, 0x666666, 0.4);
    this.hpBarFrame.strokeRect(barX - 1, barY - 1, barW + 2, barH + 2);

    // Phase markers (diamond icons at phase transition points).
    // 18 HP total: phase 2 transition at HP 12, phase 3 at HP 6.
    const phasePoints = [12, 6];
    for (const hp of phasePoints) {
      const markerX = barX + (barW / this.maxBossHP) * hp;
      const marker = this.add.text(markerX, barY + barH + 10, '◆', {
        fontSize: '14px', color: '#FF6600',
      });
      marker.setOrigin(0.5, 0).setDepth(1004).setAlpha(0.6);
      this.hpBarPhaseIcons.push(marker);
    }

    // Boss name text — cleaner Triple-A style. The old red parchment
    // "boss_v2_name_banner" cloth was removed (rejected parchment look);
    // the heavy stroke + shadow keep the name readable on its own.
    this.bossNameText = this.add.text(WIDTH / 2, barY - 24, 'POLLUTION HYDRA', {
      fontFamily: 'CF Asty Std',
      fontSize: '34px',
      color: '#F59E6C',
      stroke: '#0B1220',
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 3, color: '#050912', blur: 8, fill: true },
      letterSpacing: 4,
    });
    this.bossNameText.setOrigin(0.5, 1).setDepth(1005);

    // HP fraction text (e.g., "10/10")
    this.add.text(barX + barW + 15, barY + barH / 2, '', {
      fontSize: '20px', fontFamily: 'CF Asty Std', color: '#AAAAAA',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0, 0.5).setDepth(1005).setData('isHPCount', true);

    // AAA: Phase indicator below HP bar
    const phaseText = this.add.text(WIDTH / 2, barY + barH + 14, 'PHASE 1', {
      fontSize: '20px', fontFamily: 'CF Asty Std', color: '#88FF88',
      stroke: '#000000', strokeThickness: 3,
      letterSpacing: 2,
    });
    phaseText.setOrigin(0.5, 0).setDepth(1005);
    phaseText.setData('isPhaseText', true);

    this.hpDisplayedRatio = 1;
    this.hpDamageRatio = 1;
    this.updateHPBar();
  }

  private updateHPBar(): void {
    const { WIDTH } = GAME_CONFIG;
    const barW = 600, barH = 30, barX = (WIDTH - barW) / 2, barY = 76;
    const ratio = this.bossHP / this.maxBossHP;

    // Set target ratios
    this.hpDamageRatio = ratio; // Immediate for damage bar

    // Animate displayed ratio smoothly
    this.tweens.add({
      targets: this,
      hpDisplayedRatio: ratio,
      duration: 300,
      ease: 'Power2',
      onUpdate: () => this.drawHPBar(barX, barY, barW, barH),
    });

    // Delayed damage bar catches up after 0.5s
    this.time.delayedCall(500, () => {
      this.tweens.add({
        targets: this,
        hpDamageRatio: ratio, // Was already set, but the bar fill animation happens here
        duration: 400,
        ease: 'Power2',
        onUpdate: () => this.drawHPBar(barX, barY, barW, barH),
      });
    });

    // Update HP count text
    this.children.list.forEach((child: any) => {
      if (child.getData?.('isHPCount')) {
        child.setText(`${this.bossHP}/${this.maxBossHP}`);
        // Color by HP level
        if (ratio > 0.5) child.setColor('#88FF88');
        else if (ratio > 0.25) child.setColor('#FFAA44');
        else child.setColor('#FF4444');
      }
    });

    // Shake the HP bar on damage
    if (this.hpBarBg) {
      this.tweens.add({
        targets: this.hpBarBg,
        x: this.hpBarBg.x + (Math.random() - 0.5) * 8,
        duration: 40, yoyo: true, repeat: 3,
        onComplete: () => {
          if (this.hpBarBg) this.hpBarBg.setPosition(barX + barW / 2, barY + barH / 2);
        },
      });
    }
  }

  private drawHPBar(barX: number, barY: number, barW: number, barH: number): void {
    // Delayed damage fill (white/yellow)
    if (this.hpBarDamageFill) {
      this.hpBarDamageFill.clear();
      const dmgW = barW * Math.max(this.hpDamageRatio, this.hpDisplayedRatio + 0.02);
      this.hpBarDamageFill.fillStyle(0xFFFFAA, 0.6);
      this.hpBarDamageFill.fillRect(barX, barY, dmgW, barH);
    }

    // Main HP fill with gradient
    if (this.hpBarFill) {
      this.hpBarFill.clear();
      const fillW = barW * this.hpDisplayedRatio;
      const ratio = this.hpDisplayedRatio;

      // Color gradient based on HP
      let color1: number, color2: number;
      if (ratio > 0.5) {
        color1 = 0x44FF44; color2 = 0x22CC22; // Green
      } else if (ratio > 0.25) {
        color1 = 0xFFAA00; color2 = 0xFF7700; // Orange
      } else {
        color1 = 0xFF2222; color2 = 0xCC0000; // Red
      }

      // Fill with gradient (top lighter, bottom darker)
      this.hpBarFill.fillGradientStyle(color1, color1, color2, color2, 1, 1, 1, 1);
      this.hpBarFill.fillRect(barX, barY, fillW, barH);

      // Bright highlight on top 30% (glass effect)
      this.hpBarFill.fillStyle(0xFFFFFF, 0.15);
      this.hpBarFill.fillRect(barX, barY, fillW, barH * 0.35);
    }

    // Shimmer glow (moving highlight)
    if (this.hpBarGlow) {
      this.hpBarGlow.clear();
      const fillW = barW * this.hpDisplayedRatio;
      if (fillW > 10) {
        const shimmerX = barX + ((Date.now() * 0.05) % (fillW + 60)) - 30;
        this.hpBarGlow.fillStyle(0xFFFFFF, 0.12);
        this.hpBarGlow.fillRect(
          Math.max(barX, shimmerX), barY,
          Math.min(60, fillW - (shimmerX - barX)), barH
        );
      }
    }
  }

  /**
   * Pre-fight narrative card — Hydra portrait + lore copy from the
   * client brief, dismissed before the existing in-scene cinematic
   * fires. Hides the boss HUD while the modal is up so the card sits
   * cleanly over the arena background.
   */
  private runBossIntroCard(onDismiss: () => void): void {
    const hideHud = () => {
      const ui = this.scene.get(SCENE_KEYS.UI);
      if (ui?.cameras?.main) ui.cameras.main.setVisible(false);
    };
    const showHud = () => {
      const ui = this.scene.get(SCENE_KEYS.UI);
      if (ui?.cameras?.main) ui.cameras.main.setVisible(true);
    };
    hideHud();
    this.time.delayedCall(50, hideHud);

    const modal = new NarrativeCardModal(this, BOSS_INTRO, () => {
      showHud();
      onDismiss();
    });
    modal.show();
  }

  // ========== BOSS INTRO ==========
  private showBossIntro(): void {
    if (!this.boss) return;
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const audio = AudioManager.getInstance();

    // AAA: Triple-A thunder crack as the boss appears, layered with legacy roar
    if (this.cache.audio.exists('sfx_thunder_crack')) {
      audio.playSFX(this, 'sfx_thunder_crack', { volume: 0.85 });
    }
    this.time.delayedCall(180, () => {
      audio.playSFX(this, 'sfx_boss_roar', { volume: 0.8 });
    });

    // Cinematic letterbox bars
    const topBar = this.add.rectangle(WIDTH / 2, -50, WIDTH, 100, 0x000000).setDepth(10001);
    const bottomBar = this.add.rectangle(WIDTH / 2, HEIGHT + 50, WIDTH, 100, 0x000000).setDepth(10001);
    this.tweens.add({ targets: topBar, y: 50, duration: 300, ease: 'Power2' });
    this.tweens.add({ targets: bottomBar, y: HEIGHT - 50, duration: 300, ease: 'Power2' });

    const warning = this.add.text(WIDTH / 2, HEIGHT / 2 - 60, '⚠ WARNING ⚠', {
      fontSize: '110px', fontFamily: 'CF Asty Std', color: '#FF0000',
      stroke: '#000000', strokeThickness: 10,
    });
    warning.setOrigin(0.5).setDepth(10002).setAlpha(0).setScale(0.3);

    // Boss name during the intro cinematic. The red parchment banner that
    // used to sit behind it was removed (rejected parchment look); the bold
    // stroked text reads on its own over the letterboxed flash.
    const bossName = this.add.text(WIDTH / 2, HEIGHT / 2 + 50, 'POLLUTION HYDRA', {
      fontSize: '64px', fontFamily: 'CF Asty Std', color: '#44FF44',
      stroke: '#000000', strokeThickness: 6,
    });
    bossName.setOrigin(0.5).setDepth(10002).setAlpha(0).setScale(0.5);

    // Camera effects
    this.cameras.main.flash(500, 255, 0, 0);
    this.cameras.main.shake(800, 0.005);

    // Staggered entrance
    this.tweens.add({ targets: warning, alpha: 1, scale: 1.1, duration: 500, ease: 'Back.easeOut' });
    this.time.delayedCall(300, () => {
      // Warning pulse
      this.tweens.add({ targets: warning, scale: { from: 1.1, to: 1.2 }, duration: 300, yoyo: true, repeat: 2 });
    });
    this.tweens.add({ targets: bossName, alpha: 1, scale: 1, duration: 500, delay: 500, ease: 'Back.easeOut' });

    // Exit after dramatic hold
    this.time.delayedCall(2800, () => {
      this.tweens.add({ targets: topBar, y: -50, duration: 400, ease: 'Power2', onComplete: () => topBar.destroy() });
      this.tweens.add({ targets: bottomBar, y: HEIGHT + 50, duration: 400, ease: 'Power2', onComplete: () => bottomBar.destroy() });
      this.tweens.add({
        targets: [warning, bossName], alpha: 0, scale: 1.5, duration: 400,
        onComplete: () => {
          warning.destroy();
          bossName.destroy();
          this.bossStartTime = this.time.now;
          this.syncBossHudStatus('PHASE 1', '#9EE8F4');
          this.startBossAttacks();
        },
      });
    });
  }

  // ========== PROJECTILE ==========
  private shootProjectile(): void {
    if (this.isProjectileActive || !this.player) return;

    // Shooting cooldown — prevents spam
    const now = Date.now();
    if (now - this.lastShotTime < this.SHOT_COOLDOWN) return;
    this.lastShotTime = now;

    this.projectileSprites = [];
    this.projectileStartX = this.player.x;
    this.isProjectileActive = true;
    (this as any)._piercedBalls = new Set<Phaser.GameObjects.Arc>(); // reset pierce tracking each shot

    if (this.player.type === 'Sprite' && this.anims.exists(ANIMATION_KEYS.PLAYER.SHOOT)) {
      this.playerState = 'shooting';
      this.player.play(ANIMATION_KEYS.PLAYER.SHOOT);
      this.player.once('animationcomplete', () => {
        if (this.player && this.anims.exists(ANIMATION_KEYS.PLAYER.IDLE)) this.player.play(ANIMATION_KEYS.PLAYER.IDLE);
        this.playerState = 'idle';
      });
    }

    const firstProj = this.add.image(this.projectileStartX, this.player.y, ASSET_KEYS.ENERGY_PROJECTILE);
    firstProj.setScale(1.0).setDepth(-1).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.9);
    this.projectileSprites.push(firstProj);

    this.time.delayedCall(PROJECTILE_CONFIG.MAX_LIFETIME, () => {
      if (this.isProjectileActive) this.destroyProjectile();
    });
  }

  private updateProjectile(): void {
    if (!this.isProjectileActive || this.projectileSprites.length === 0) return;

    const lastSprite = this.projectileSprites[this.projectileSprites.length - 1];
    const spriteH = PROJECTILE_CONFIG.SPRITE_SIZE + PROJECTILE_CONFIG.SPRITE_SPACING;
    const newY = lastSprite.y - spriteH;

    if (newY >= PHYSICS_CONFIG.CEILING_Y) {
      const newProj = this.add.image(this.projectileStartX, newY, ASSET_KEYS.ENERGY_PROJECTILE);
      newProj.setScale(1.0).setDepth(-1).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.9);
      this.projectileSprites.push(newProj);
    } else {
      this.time.delayedCall(PROJECTILE_CONFIG.HOLD_DURATION, () => this.destroyProjectile());
    }
  }

  private destroyProjectile(): void {
    this.projectileSprites.forEach(s => s.destroy());
    this.projectileSprites = [];
    this.isProjectileActive = false;
  }

  // ========== PLAYER DAMAGE ==========
  private playerHit(damage: number = 1): void {
    if (this.isInvincible || designModeState.isDesignMode()) return;

    // AAA: Player hit SFX + screen shake
    AudioManager.getInstance().playSFX(this, 'sfx_player_hit', { volume: 0.7 });
    this.cameras.main.shake(200, 0.008);

    // ── ENERGY-BASED DAMAGE: drain energy first, consume life on depletion ──
    const uiScene = this.scene.get(SCENE_KEYS.UI) as any;
    const depleted: boolean = uiScene?.drainEnergy ? !!uiScene.drainEnergy(damage) : true;

    if (!depleted) {
      // Took damage, still kicking — short invincibility + minor knockback
      this.isInvincible = true;
      if (this.player) {
        const flash = this.tweens.add({
          targets: this.player, alpha: { from: 0.3, to: 1 },
          duration: LIVES_CONFIG.FLASH_INTERVAL, yoyo: true,
          repeat: Math.floor(800 / (LIVES_CONFIG.FLASH_INTERVAL * 2)),
        });
        this.time.delayedCall(900, () => {
          this.isInvincible = false;
          if (this.player) this.player.setAlpha(1);
          flash.stop();
        });
      }
      if (screenShakeState.isEnabled()) {
        this.cameras.main.flash(140, 255, 80, 80);
      }
      return;
    }

    // Energy depleted — consume a continue
    this.lives--;
    this.isInvincible = true;

    try { if (uiScene?.updateLives) uiScene.updateLives(this.lives); } catch {}
    this.updateLifeStarsHud();

    if (this.lives <= 0) { this.gameOver(); return; }

    // Continues remain — refill energy + long invincibility + drama
    try { if (uiScene?.refillEnergy) uiScene.refillEnergy(); } catch {}

    if (this.player) {
      this.player.x = PLAYER_CONFIG.START_X;
      const flash = this.tweens.add({
        targets: this.player, alpha: { from: 0.3, to: 1 },
        duration: LIVES_CONFIG.FLASH_INTERVAL, yoyo: true,
        repeat: Math.floor(LIVES_CONFIG.INVINCIBILITY_DURATION / (LIVES_CONFIG.FLASH_INTERVAL * 2)),
      });
      this.time.delayedCall(LIVES_CONFIG.INVINCIBILITY_DURATION, () => {
        this.isInvincible = false;
        if (this.player) this.player.setAlpha(1);
        flash.stop();
      });
    }

    if (screenShakeState.isEnabled()) {
      this.cameras.main.shake(280, 0.012);
      this.cameras.main.flash(320, 255, 100, 100);
    }
  }

  private gameOver(): void {
    this.isGameOver = true;

    // Kill ALL tweens first — prevents callbacks from firing on destroyed objects
    // and corrupting the scene event loop (same pattern as GameScene)
    this.tweens.killAll();

    if (this.bossAttackTimer) this.bossAttackTimer.remove();
    if (this.autoShootTimer) this.autoShootTimer.remove();

    // Capture refs before any scene state changes
    const game = this.game;
    const score = this.score;

    // AAA: Autoplay restart — never show game-over screen, loop back to stage 1
    if (this.isAutoPlay) {
      console.log('🤖 AUTO-PLAY: Boss game-over → restarting autoplay from stage 1');
      // Use raw setTimeout — scene timers are unreliable after game over
      setTimeout(() => {
        try {
          game.scene.stop(SCENE_KEYS.UI);
          setTimeout(() => {
            game.scene.stop(SCENE_KEYS.BOSS);
            setTimeout(() => {
              game.scene.start(SCENE_KEYS.GAME, { stage: 1, score: 0, autoPlay: true });
            }, 50);
          }, 50);
        } catch (e) {
          console.error('AUTO-PLAY restart failed:', e);
        }
      }, 1500);
      return;
    }

    // Stop the HUD immediately so it does not linger in front of the fade,
    // then hold the dying arena on screen for 3 seconds and fade it to black
    // before the game-over card appears. The gap (a) lets the player register
    // the loss, (b) gives them time to release the shoot button and the
    // stick so leftover joystick input does not land on a CTA the instant
    // the card pops up. The card itself fades in from black over 400ms via
    // GameOverScene.fadeIn, so the transition reads as one continuous beat.
    game.scene.stop(SCENE_KEYS.UI);
    this.cameras.main.fadeOut(2500, 0, 0, 0);
    // Raw setTimeout because scene timers are unreliable after tweens.killAll.
    setTimeout(() => {
      try {
        game.scene.stop(SCENE_KEYS.BOSS);
        setTimeout(() => {
          game.scene.start(SCENE_KEYS.GAME_OVER, { score, reason: 'Defeated by Hydra', stage: 7 });
        }, 50);
      } catch (e) {
        console.error('Boss game-over transition failed:', e);
        game.scene.start(SCENE_KEYS.MENU);
      }
    }, 3000);
  }

  private async submitBossScore(): Promise<void> {
    const playerId = this.registry.get('playerId');
    const playerName = this.registry.get('playerName');
    if (!playerId || !playerName) return;
    // Run-level row for the CSV / global high scores (one per run).
    await submitScore({
      playerId, playerName, stageNumber: 7,
      stageScore: this.score - this.startingScore,
      totalScore: this.score, timeRemaining: 0,
      livesRemaining: this.lives, completed: true,
    });
    // Per-stage row for the boss (stage 7) leaderboard.
    await submitStageScore({
      playerId, playerName, stageNumber: 7,
      stageScore: this.score - this.startingScore,
      timeRemaining: 0,
    });
  }
}
